// English language pack
export default {
  site: {
    title: 'MoonRise Tools - Practical Online Utilities',
    name: 'MoonRise Tools',
    slogan: 'Practical online utilities for work and life'
  },
  nav: {
    home: 'Home',
    json: 'JSON Formatter',
    timestamp: 'Timestamp',
    wordcount: 'Word Count',
    calculator: 'Calculator',
    about: 'About Us',
    privacy: 'Privacy Policy',
    blog: 'Blog'
  },
  theme: {
    light: 'Switch to Light Mode',
    dark: 'Switch to Dark Mode'
  },
  language: {
    switch: 'Switch Language',
    zh: '中文',
    en: 'English'
  },
  footer: {
    copyright: '© 2026 MoonRise Tools - Practical Online Utilities'
  },
  donate: {
    button: 'Buy me a coffee',
    title: 'Buy Me a Coffee ☕',
    subtitle: 'If these tools are helpful, feel free to support me~',
    wechat: 'WeChat',
    alipay: 'Alipay',
    scanHint: 'Scan with your phone to donate any amount',
    thanks: 'Thank you for your support! It keeps me going! ❤️'
  },
  common: {
    copy: 'Copy',
    copyResult: 'Copy Result',
    clear: 'Clear',
    calculate: 'Calculate',
    convert: 'Convert',
    copied: 'Copied to clipboard!',
    copyFailed: 'Copy failed, please copy manually',
    inputPlaceholder: 'Please enter...',
    resultPlaceholder: 'Result will be displayed here...',
    error: 'Error',
    success: 'Success',
    loading: 'Loading...',
    refresh: 'Refresh'
  },
  home: {
    welcome: 'Welcome to MoonRise Tools',
    intro: 'MoonRise Tools is a website dedicated to providing free and practical online tools. We have carefully developed a variety of useful tools covering developer tools, text processing, image processing, and daily life calculations. All tools can be used online without downloading or installing any software.',
    introDetail: 'Our tools are completely free and require no registration or login. More importantly, all data you input is processed locally in your browser and will not be uploaded to any server, fully protecting your privacy. Whether you are a programmer who needs to format JSON data or an ordinary user who needs to calculate mortgage payments, MoonRise Tools can provide you with convenient solutions.',
    features: {
      free: 'Always Free',
      secure: 'Privacy Safe',
      instant: 'Ready to Use',
      responsive: 'Multi-device'
    },
    sections: {
      devTools: 'Developer Tools',
      devToolsDesc: 'Practical tools designed for programmers and developers to help you handle various data format conversions and validations in your daily development work.',
      textTools: 'Text Processing',
      textToolsDesc: 'Various text processing and conversion tools to meet your needs in writing, editing, and document processing.',
      imageTools: 'Image Processing',
      imageToolsDesc: 'Online image processing tools. Complete image compression, cropping, and filter effects directly in your browser.',
      lifeTools: 'Life Tools',
      lifeToolsDesc: 'Practical daily life calculation tools to help you easily complete mortgage calculations, tax calculations, health assessments, and currency conversions.'
    },
    tools: {
      json: {
        title: 'JSON Formatter',
        desc: 'Online validate, format, and compress JSON data with syntax highlighting to help developers quickly check and beautify JSON data.'
      },
      timestamp: {
        title: 'Timestamp Converter',
        desc: 'Online conversion between Unix timestamps and standard date-time formats. Supports both second and millisecond timestamps, essential for development and debugging.'
      },
      calculator: {
        title: 'Calculator',
        desc: 'Powerful online scientific calculator supporting basic operations and advanced mathematical functions with a clean and easy-to-use interface.'
      },
      wordcount: {
        title: 'Word Count',
        desc: 'Online tool for counting words, characters, paragraphs, and lines. Supports mixed Chinese and English text, perfect for writers and editors.'
      },
      markdown: {
        title: 'Markdown Editor',
        desc: 'Online Markdown editor with real-time preview. Create beautifully formatted documents with support for common Markdown syntax.'
      },
      traditional: {
        title: 'Chinese Converter',
        desc: 'Convert between Simplified and Traditional Chinese characters. Supports batch conversion for cross-strait communication needs.'
      },
      uppercase: {
        title: 'Amount Converter',
        desc: 'Convert Arabic numerals to Chinese uppercase amounts, meeting financial standards for checks, invoices, and formal documents.'
      },
      image: {
        title: 'Image Processing',
        desc: 'Online image compression, cropping, and filter tools. Supports JPG, PNG, and other common formats with local processing for privacy.'
      },
      qrGenerator: {
        title: 'QR Generator',
        desc: 'Generate QR codes online with customizable size, colors, and error correction level. Download as PNG or SVG.'
      },
      qrScanner: {
        title: 'QR Scanner',
        desc: 'Upload or drag an image to decode QR codes. Supports pasting screenshots. All processing done locally.'
      },
      mortgage: {
        title: 'Mortgage Calculator',
        desc: 'Professional mortgage calculator supporting equal principal and interest and equal principal repayment methods with detailed monthly breakdown.'
      },
      tax: {
        title: 'Tax Calculator',
        desc: 'Calculate personal income tax based on the latest tax laws with support for special deductions to understand your actual take-home pay.'
      },
      bmi: {
        title: 'BMI Calculator',
        desc: 'Online Body Mass Index (BMI) calculator that evaluates health status based on height and weight with scientific weight management advice.'
      },
      exchange: {
        title: 'Exchange Rate',
        desc: 'Multi-currency real-time exchange rate converter supporting USD, EUR, JPY, HKD, and other major currencies with live rate updates.'
      },
      taxRate: {
        title: 'Tax Rate Calculator',
        desc: 'Calculate tax amounts from gross or net amounts with common VAT rates. Quick tax-inclusive/exclusive conversion.'
      },
      listSum: {
        title: 'List Sum',
        desc: 'Quickly sum a list of numbers with average, max, min, and median statistics. Supports multiple separators.'
      },
      loanCalc: {
        title: 'Loan Calculator',
        desc: 'Universal loan repayment calculator supporting equal installment and equal principal methods with detailed schedule.'
      },
      textDedup: {
        title: 'Text Dedup',
        desc: 'Quickly remove duplicate lines from text. Supports case-insensitive matching and empty line filtering for data cleaning.'
      },
      textReplace: {
        title: 'Text Replace',
        desc: 'Powerful online find and replace tool with support for plain text and regular expression matching for efficient batch processing.'
      },
      textSort: {
        title: 'Text Sort',
        desc: 'Sort text lines in ascending, descending, reverse, or random order. Supports alphabetical and numeric sorting.'
      },
      textNumber: {
        title: 'Line Numbers',
        desc: 'Automatically add sequential numbers to each line of text with multiple numbering formats and customizable start numbers.'
      },
      text2html: {
        title: 'Text to HTML',
        desc: 'Convert plain text to HTML format with support for paragraphs, lists, line breaks, and table conversion modes.'
      },
      wordFreq: {
        title: 'Word Frequency',
        desc: 'Analyze word frequency distribution in text with support for Chinese and English, visualized frequency ranking display.'
      },
      namingConverter: {
        title: 'Naming Converter',
        desc: 'Convert between camelCase, PascalCase, snake_case, kebab-case and other programming naming conventions.'
      },
      regexTester: {
        title: 'Regex Tester',
        desc: 'Test and debug regular expressions online with real-time highlighting and capture group inspection.'
      },
      colorConverter: {
        title: 'Color Converter',
        desc: 'Convert between HEX, RGB, RGBA, HSL color formats with live preview and color picker.'
      },
      codeFormatter: {
        title: 'Code Formatter',
        desc: 'Format (beautify) and compress (minify) CSS, JavaScript, HTML, and SQL code online.'
      },
      crontab: {
        title: 'Crontab Parser',
        desc: 'Parse Crontab expressions, preview execution times, with commonly used expression presets.'
      },
      base64: {
        title: 'Base64 Codec',
        desc: 'Online Base64 encode and decode tool with support for Unicode and special characters.'
      },
      urlCodec: {
        title: 'URL Codec',
        desc: 'Online URL encode and decode tool supporting encodeURI and encodeURIComponent modes.'
      },
      hashTool: {
        title: 'MD5/SHA Hash',
        desc: 'Calculate MD5, SHA-1, SHA-256, SHA-512 hash values online with uppercase/lowercase toggle.'
      },
      unicodeConverter: {
        title: 'Unicode Converter',
        desc: 'Convert between Unicode, Hex, HTML entities, and UTF-8 encodings for any characters.'
      },
      morseCode: {
        title: 'Morse Code',
        desc: 'Convert text to Morse code and vice versa with audio playback and reference chart.'
      },
      passwordGen: {
        title: 'Password Generator',
        desc: 'Generate secure random passwords with customizable length, character types, strength meter, and batch generation.'
      },
      uuidGen: {
        title: 'UUID Generator',
        desc: 'Generate UUID/GUID online with v1 (time-based) and v4 (random) versions, batch generation and format options.'
      }
    },
    usage: {
      title: 'How to Use MoonRise Tools',
      step1: {
        title: 'Choose a Tool',
        desc: 'Browse the various tools on the homepage and click on the tool card you need to enter the corresponding function page.'
      },
      step2: {
        title: 'Enter Data',
        desc: 'Enter the data you need to process on the tool page. All data processing is done locally in your browser to ensure privacy.'
      },
      step3: {
        title: 'Get Results',
        desc: 'The tool will instantly display the processing results. You can copy the results or continue with other operations, simple and efficient.'
      }
    },
    faq: {
      title: 'FAQ',
      q1: {
        question: 'Is MoonRise Tools free?',
        answer: 'Yes, all tools provided by MoonRise Tools are completely free. You can use any feature without limitations and without registration or login.'
      },
      q2: {
        question: 'Is my data safe?',
        answer: 'Absolutely. All our tools run locally in your browser. Your input data is never uploaded to any server. You have complete control over your privacy.'
      },
      q3: {
        question: 'Can I use it on mobile?',
        answer: 'Of course. MoonRise Tools uses responsive design, perfectly adapting to computers, tablets, and mobile phones. Use it anytime, anywhere.'
      },
      q4: {
        question: 'Will tools be updated?',
        answer: 'Yes, we will continue to develop new practical tools based on user needs and constantly optimize the functionality and experience of existing tools. Suggestions are welcome.'
      }
    }
  },
  json: {
    title: 'JSON Formatter',
    subtitle: 'Online validate, format, and compress JSON data with syntax highlighting, an essential tool for developers',
    format: 'Format',
    compress: 'Compress',
    inputLabel: 'Input JSON',
    outputLabel: 'Output Result',
    inputPlaceholder: 'Enter or paste JSON data...',
    outputPlaceholder: 'Formatted result will be displayed here...',
    indentSpaces: 'Indent Spaces',
    formatSuccess: 'JSON formatted successfully!',
    compressSuccess: 'JSON compressed successfully!',
    emptyError: 'Please enter JSON data',
    formatError: 'JSON format error',
    whatIsJson: 'What is JSON?',
    whatIsJsonDesc: 'JSON (JavaScript Object Notation) is a lightweight data interchange format, first proposed by Douglas Crockford in 2001. JSON is based on a subset of JavaScript but is a language-independent text format supported by virtually all modern programming languages. JSON has become one of the most commonly used data formats on the internet, widely used in Web APIs, configuration files, data storage, front-end and back-end communication, and more.',
    features: 'JSON Formatter Features',
    featureFormat: 'Format (Beautify): Convert compressed JSON data into indented, readable format for easy viewing of data structure and debugging.',
    featureCompress: 'Compress (Minify): Remove spaces and line breaks from JSON to reduce data size, suitable for network transmission and storage.',
    featureValidate: 'Syntax Validation: Automatically detect if JSON format is correct, helping you quickly find syntax errors.',
    featureCopy: 'One-click Copy: Copy results with one click after processing for easy use elsewhere.',
    howToUse: 'How to Use This Tool',
    howToUseSteps: [
      'Paste or enter your JSON data in the left text box.',
      'Click "Format" to beautify JSON, or "Compress" to minify JSON.',
      'If there is a JSON format error, the tool will display the specific error message.',
      'After successful processing, the result will be displayed in the right text box. Click "Copy Result" to copy.'
    ],
    example: 'JSON Format Example',
    exampleDesc: 'A standard JSON object example:',
    commonErrors: 'Common JSON Errors',
    errorQuotes: 'Missing quotes: JSON keys must be surrounded by double quotes. Single quotes or no quotes are incorrect.',
    errorTrailingComma: 'Trailing comma: There cannot be a comma after the last element of an array or object.',
    errorSingleQuote: 'Using single quotes: JSON only supports double quotes, not single quotes.',
    errorComments: 'Comments: Standard JSON does not support comments. Adding comments will cause parsing to fail.',
    jsonHistory: 'History of JSON',
    jsonHistoryDesc: 'JSON was born in 2001, initially designed as a lightweight alternative to XML. In 2006, JSON was officially defined as RFC 4627 standard. In 2013, the ECMA-404 standard was published to further standardize JSON syntax. Today, JSON has completely replaced XML as the mainstream data exchange format in web development.',
    jsonVsXml: 'JSON vs XML Comparison',
    jsonVsXmlItems: [
      'Smaller size: JSON is typically 30%-50% smaller than XML for the same data, saving network bandwidth.',
      'Faster parsing: JSON can be parsed directly by JavaScript without additional parsers, making processing faster.',
      'More readable: JSON syntax is concise and intuitive, making it easy for humans to read and write.',
      'Native support: Modern browsers and programming languages have built-in JSON parsing capabilities.'
    ],
    jsonDataTypes: 'JSON Data Types',
    jsonDataTypesItems: [
      'String: Text enclosed in double quotes, e.g., "hello world"',
      'Number: Integer or floating-point, e.g., 42, 3.14, -100',
      'Boolean: true or false',
      'Null: null represents empty or no value',
      'Array: An ordered list of values in square brackets, e.g., [1, 2, 3]',
      'Object: A collection of key-value pairs in curly braces, e.g., {"name": "John"}'
    ],
    jsonUseCases: 'Common JSON Use Cases',
    jsonUseCasesItems: [
      'Web API data transfer: RESTful APIs almost universally use JSON',
      'Configuration files: package.json, tsconfig.json, etc.',
      'Data storage: NoSQL databases like MongoDB use JSON format',
      'Front-end/back-end communication: Ajax requests typically use JSON',
      'Logging: Structured logs often use JSON format for easy analysis',
      'Message queues: Kafka, RabbitMQ often use JSON for messages'
    ],
    jsonBestPractices: 'JSON Best Practices',
    jsonBestPracticesItems: [
      'Use meaningful key names: Keys should clearly express data meaning, e.g., "userName" not "u"',
      'Keep data structures consistent: Similar data should maintain the same structure',
      'Avoid deep nesting: Too much nesting increases parsing complexity, recommend 4-5 levels max',
      'Use arrays for lists: Multiple elements of the same type should use arrays',
      'Use ISO 8601 for dates: e.g., "2024-01-15T10:30:00Z" for cross-platform parsing'
    ]
  },
  timestamp: {
    title: 'Timestamp Converter',
    subtitle: 'Online conversion between Unix timestamps and standard date-time formats, supporting both second and millisecond timestamps',
    currentTimestamp: 'Current Timestamp',
    toDate: 'Timestamp → Date Time',
    toTimestamp: 'Date Time → Timestamp',
    inputPlaceholder: 'Enter timestamp (seconds or milliseconds)',
    seconds: 'Seconds (s)',
    milliseconds: 'Milliseconds (ms)',
    localTime: 'Local Time',
    utcTime: 'UTC Time',
    isoFormat: 'ISO Format',
    secondsTimestamp: 'Seconds Timestamp',
    millisecondsTimestamp: 'Milliseconds Timestamp',
    currentTime: 'Current Time',
    invalidNumber: 'Please enter a valid number',
    invalidTimestamp: 'Invalid timestamp',
    conversionFailed: 'Conversion failed',
    whatIsTimestamp: 'What is Unix Timestamp?',
    whatIsTimestampDesc: 'Unix Timestamp, also called POSIX time or Epoch time, is a way of representing time. It is defined as the total number of seconds elapsed since January 1, 1970 00:00:00 UTC (Coordinated Universal Time), not counting leap seconds. This starting point is called the "Unix Epoch". Since a timestamp is a simple integer independent of time zones, it is very convenient and reliable for passing time information between different systems and regions.',
    useCases: 'Use Cases for Timestamps',
    useCaseDb: 'Database Storage: Many databases use timestamps to store time information because integers save more space and have higher query efficiency than date strings.',
    useCaseApi: 'API Interfaces: Web APIs usually use timestamps to pass time parameters, avoiding problems caused by time zone conversions.',
    useCaseLog: 'Logging: System logs often use timestamps to record the exact time of events.',
    useCaseCache: 'Cache Control: Browsers and servers use timestamps to determine if cache has expired.',
    comparison: 'Seconds vs Milliseconds Timestamps',
    comparisonDesc: 'Timestamps have two common precisions:',
    comparisonSeconds: 'Seconds timestamp (10 digits): e.g., 1707235200, accurate to seconds, commonly used in Unix/Linux systems and most backend programming languages.',
    comparisonMilliseconds: 'Milliseconds timestamp (13 digits): e.g., 1707235200000, accurate to milliseconds, commonly used in JavaScript and scenarios requiring higher precision.',
    programmingExamples: 'Getting Timestamps in Different Languages',
    timestampHistory: 'Origin of Timestamps',
    timestampHistoryDesc: 'January 1, 1970 was chosen as the Unix epoch starting point mainly because the Unix operating system was originally developed at Bell Labs by Ken Thompson and Dennis Ritchie in 1969-1970. They chose a "recent" and "easy to remember" time point as the starting point. This design decision has been used ever since and has become a standard in the computing field.',
    y2038Problem: 'The Year 2038 Problem',
    y2038ProblemDesc: 'The maximum timestamp value stored in 32-bit systems is 2147483647 (2^31-1), corresponding to January 19, 2038 03:14:07 UTC. At that time, 32-bit system timestamps will overflow, similar to the "Y2K" problem. The good news is that most modern systems already use 64-bit integers to store timestamps, which can represent time up to about 292 billion years in the future.',
    timezoneExplain: 'Timestamps and Time Zones',
    timezoneExplainDesc: 'A major advantage of timestamps is that they are time zone independent. Whether you are in Beijing, New York, or London, the timestamp for the same moment is identical. When displaying time, applications convert timestamps to local time based on the user\'s time zone. This is why using timestamps can avoid a lot of confusion when collaborating across time zones.',
    commonTimestamps: 'Notable Timestamps',
    commonTimestampsItems: [
      '0: 1970-01-01 00:00:00 UTC (Unix Epoch)',
      '1000000000: 2001-09-09 01:46:40 UTC (1 billion seconds)',
      '1234567890: 2009-02-13 23:31:30 UTC (famous "1234567890" moment)',
      '2147483647: 2038-01-19 03:14:07 UTC (32-bit system maximum)'
    ],
    timestampTips: 'Tips for Using Timestamps',
    timestampTipsItems: [
      'Store UTC timestamps and convert to local time when displaying',
      'Use millisecond or even microsecond timestamps for high-precision scenarios like financial transactions',
      'Index timestamp fields in databases to significantly improve time range query performance',
      'Ensure timestamp precision consistency between front-end and back-end - JavaScript uses milliseconds, some backend languages use seconds'
    ]
  },
  wordcount: {
    title: 'Word Count',
    subtitle: 'Count characters, words, lines and more detailed text statistics',
    totalChars: 'Total Characters',
    charsNoSpace: 'Characters (no space)',
    chineseChars: 'Chinese Characters',
    englishChars: 'English Letters',
    englishWords: 'English Words',
    numbers: 'Numbers',
    lines: 'Lines',
    paragraphs: 'Paragraphs',
    inputLabel: 'Input Text',
    inputPlaceholder: 'Enter or paste text here for real-time word count...',
    tip: 'Tip: Supports mixed Chinese and English counting. Chinese counts by character, English counts by word.',
    whyWordCount: 'Why Word Count Matters?',
    whyWordCountDesc: 'Word counting is a crucial feature in writing and content creation. Whether you\'re a student writing papers, an author writing novels, an editor reviewing manuscripts, or a social media manager writing posts, you need to monitor word count. Different scenarios have different requirements, and accurate word counting helps you better control content length.',
    wordCountUseCases: 'Word Count Applications',
    wordCountUseCasesItems: [
      'Academic papers: Theses and journal articles typically have strict word requirements',
      'Social media: Platforms like Twitter have character limits for posts',
      'Translation work: Translation fees are often calculated by word count',
      'SEO optimization: Search engines have preferences for article length',
      'Publishing: Book editors need to control chapter and total word counts'
    ],
    countingRules: 'Chinese vs English Counting Rules',
    countingRulesDesc: 'Chinese and English have different counting rules:',
    countingRulesItems: [
      'Chinese: Usually counted by character, one Chinese character equals one word',
      'English: Usually counted by word, separated by spaces',
      'Mixed text: Chinese counted by character, English counted by word',
      'Punctuation: May or may not be included depending on requirements'
    ],
    writingTips: 'Recommended Word Counts for Different Content',
    writingTipsItems: [
      'Blog posts: 1500-3000 words for easy reading',
      'Long-form articles: 500-2000 words for quality answers',
      'SEO-friendly articles: 1000-2500 words recommended',
      'Press releases: Usually 300-800 words, concise and clear',
      'Product descriptions: 500-1500 words depending on complexity'
    ],
    platformLimits: 'Platform Character Limits Quick Reference',
    platformLimitsItems: [
      'Twitter/X: 280 characters per tweet, threads for longer content',
      'Instagram: Captions up to 2,200 characters, bio up to 150 characters',
      'LinkedIn: Posts up to 3,000 characters, articles up to 125,000 characters',
      'YouTube: Titles up to 100 characters, descriptions up to 5,000 characters',
      'Reddit: Post titles up to 300 characters, comments up to 10,000 characters',
      'Facebook: Posts up to 63,206 characters, but shorter posts get more engagement'
    ],
    readingTime: 'Reading Time Estimation',
    readingTimeDesc: 'Understanding reading time helps you plan article length better. Studies show that English reading speed averages 200-300 words per minute, while Chinese reading speed is about 300-500 characters per minute. This means a 1,500-word English article takes about 5-7 minutes to read. The average internet user\'s attention span is about 8 seconds, so the opening of your article is especially important — you need to hook readers within the first 100 words.',
    seoWordCount: 'Word Count and SEO',
    seoWordCountDesc: 'Search Engine Optimization (SEO) is closely related to content length. Research data shows that top-ranking web pages average 1,500-2,500 words in content length. However, more words aren\'t always better — what matters is content quality and relevance. Google and other search engines prioritize whether content comprehensively answers users\' search queries, rather than simple word count padding. A high-quality 1,000-word article often ranks better than a low-quality 3,000-word one.',
    writingEfficiency: 'Tips for Improving Writing Efficiency',
    writingEfficiencyItems: [
      'Outline first: Create an article outline before writing, clarifying key points and estimated word counts for each section',
      'Pomodoro Technique: Focus on writing for 25 minutes, rest for 5 minutes, maintaining high output',
      'Write in segments: Break long articles into independent paragraphs and complete them one by one',
      'Write first, edit later: Don\'t obsess over word choice in the first draft, revise after completing the full text',
      'Use tools: Use word count tools to monitor progress in real-time, ensuring you meet target word counts'
    ]
  },
  calculator: {
    title: 'Calculator',
    subtitle: 'Online calculator supporting basic and scientific calculations',
    basic: 'Basic',
    scientific: 'Scientific',
    history: 'History',
    clearHistory: 'Clear',
    whatIsCalculator: 'About This Calculator',
    whatIsCalculatorDesc: 'This is a powerful online calculator supporting both basic arithmetic and scientific calculations. No software installation required - just open the webpage and start calculating. With a clean interface and easy operation, it\'s perfect for daily calculations, studying, and work.',
    basicFunctions: 'Basic Calculation Functions',
    basicFunctionsItems: [
      'Addition (+): Calculate the sum of two or more numbers',
      'Subtraction (-): Calculate the difference between two numbers',
      'Multiplication (×): Calculate the product of two or more numbers',
      'Division (÷): Calculate the quotient of two numbers',
      'Percentage (%): Calculate percentage values'
    ],
    scientificFunctions: 'Scientific Calculation Functions',
    scientificFunctionsItems: [
      'Trigonometric functions: sin, cos, tan and their inverse functions',
      'Logarithmic functions: log (common), ln (natural)',
      'Exponential operations: power, square root, cube root',
      'Constants: π (pi), e (Euler\'s number)',
      'Factorial: n! calculation'
    ],
    calculatorTips: 'Usage Tips',
    calculatorTipsItems: [
      'Use keyboard to input numbers and operators for faster entry',
      'Supports parentheses - complex expressions are calculated with correct precedence',
      'History feature lets you view and reuse previous calculations',
      'Trigonometric functions use radians by default in scientific mode'
    ]
  },
  mortgage: {
    title: 'Mortgage Calculator',
    subtitle: 'Professional mortgage calculator supporting equal payment and equal principal methods with detailed repayment schedule',
    loanAmount: 'Loan Amount',
    loanYears: 'Loan Term',
    interestRate: 'Annual Rate',
    paymentMethod: 'Payment Method',
    equalPayment: 'Equal Payment',
    equalPrincipal: 'Equal Principal',
    calculate: 'Calculate',
    results: 'Results',
    monthlyPayment: 'Monthly Payment',
    firstMonthPayment: 'First Month Payment',
    totalLoan: 'Total Loan',
    totalInterest: 'Total Interest',
    totalPayment: 'Total Payment',
    details: 'Payment Schedule (First 12 months)',
    period: 'Period',
    payment: 'Payment',
    principal: 'Principal',
    interest: 'Interest',
    remaining: 'Remaining',
    unit: {
      tenThousand: '10K CNY',
      year: 'Years',
      percent: '%'
    },
    quickYears: '{n} Yrs',
    housingFund: 'Housing Fund',
    commercial: 'Commercial',
    whatIsMortgage: 'What is a Mortgage?',
    whatIsMortgageDesc: 'A mortgage (housing loan) is a loan provided by banks or financial institutions to home buyers, using the purchased property as collateral. Buyers must repay the principal and interest in installments according to the agreed term and rate. Mortgages are the primary financing method for most families purchasing property, and understanding mortgage calculations is crucial for sound family financial planning.',
    repaymentMethods: 'Two Repayment Methods Explained',
    equalPaymentExplain: 'Equal Payment (Amortization)',
    equalPaymentExplainDesc: 'Equal payment means the monthly payment amount remains constant. In early payments, interest accounts for a larger portion while principal is smaller; over time, interest decreases and principal increases. This method suits buyers with stable income who want fixed monthly payments for easier financial planning.',
    equalPrincipalExplain: 'Equal Principal',
    equalPrincipalExplainDesc: 'Equal principal means the monthly principal payment is fixed, while interest decreases as the remaining balance decreases. Therefore, monthly payments decrease over time, with higher pressure in early payments. This method results in less total interest paid and suits buyers who can handle higher early payments and want to minimize total interest.',
    comparisonTitle: 'Comparison of Two Methods',
    comparisonItems: [
      'Total interest: Equal principal typically has 10%-15% less total interest than equal payment',
      'Monthly pressure: Equal payment is fixed, equal principal starts high and decreases',
      'Best for: Stable income → equal payment; decreasing income or want less interest → equal principal',
      'Early payoff: Equal principal is better if you plan to pay off early'
    ],
    interestRateTypes: 'Types of Loan Interest Rates',
    interestRateTypesDesc: 'China currently has two main mortgage rates: housing fund loan rate and commercial loan rate. Housing fund rates are lower (currently about 3.1%) but with limited amounts; commercial loan rates are higher (currently LPR benchmark about 3.45%-4.2%) but with sufficient amounts. Many buyers choose combined loans using both.',
    prepaymentStrategies: 'Early Repayment Strategies',
    prepaymentStrategiesItems: [
      'Shorten term vs reduce payment: Shortening the term saves more interest; reducing payment lowers pressure',
      'Best timing: Early repayment works best in the first 1/3 of the loan term when interest proportion is highest',
      'Prepayment penalties: Some banks charge penalties for early repayment - check policies first',
      'Weigh alternatives: If investment returns consistently exceed loan rate, early repayment may not be optimal'
    ],
    buyingTips: 'Home Loan Advice',
    buyingTipsItems: [
      'Keep monthly payments under 30%-40% of household monthly income to maintain adequate living funds',
      'Pay more down payment when possible to reduce principal and total interest',
      'Choose loan term considering age, income growth expectations, and retirement plans',
      'Monitor LPR rate changes and choose appropriate rate reset dates',
      'Check your credit report before applying - good credit helps secure better rates'
    ],
    lprExplain: 'About LPR',
    lprExplainDesc: 'LPR (Loan Prime Rate) is the benchmark lending rate authorized by the People\'s Bank of China, announced on the 20th of each month. Mortgage rate = LPR + bank markup. The current 5-year+ LPR is 3.45% (2024 data, for reference only). Rate reset dates are typically January 1st each year or the loan anniversary date.'
  },
  tax: {
    title: 'Tax Calculator',
    subtitle: 'Calculate personal income tax (2024 latest rates)',
    grossSalary: 'Gross Salary',
    insurance: 'Social Insurance',
    insuranceTip: 'Including pension, medical, unemployment insurance and housing fund',
    specialDeduction: 'Special Deductions',
    calculate: 'Calculate Tax',
    results: 'Results',
    taxPayable: 'Tax Payable',
    netSalary: 'Net Salary',
    details: 'Calculation Details',
    threshold: 'Tax Threshold',
    taxableIncome: 'Taxable Income',
    taxRate: 'Tax Rate',
    quickDeduction: 'Quick Deduction',
    taxTable: 'Personal Income Tax Rate Table',
    level: 'Level',
    taxableRange: 'Taxable Income Range',
    deductions: {
      children: "Children's Education 2000/month",
      education: 'Continuing Education 400/month',
      medical: 'Medical (Actual)',
      housing: 'Housing Loan Interest 1000/month',
      rent: 'Housing Rent 1500/month',
      elderly: 'Elderly Support 3000/month',
      baby: 'Infant Care 2000/month'
    },
    unit: 'CNY',
    whatIsTax: 'What is Personal Income Tax?',
    whatIsTaxDesc: 'Personal income tax is a tax levied on various taxable income of individuals. In China, wages and salaries are the most common taxable items. Since January 1, 2019, China implemented a new personal income tax law, raising the threshold from 3,500 to 5,000 yuan and introducing a special additional deduction system to make taxation fairer and more reasonable.',
    taxFormula: 'Tax Calculation Formula',
    taxFormulaDesc: 'Tax payable = Taxable income × Applicable rate - Quick deduction. Taxable income = Pre-tax income - Social insurance - Threshold (5,000 yuan) - Special deductions.',
    specialDeductionsExplain: 'Special Deductions Explained',
    specialDeductionsItems: [
      'Children\'s education: 2,000 yuan/month per child, from age 3 to PhD graduation, can be split 50/50 between spouses or 100% by one',
      'Continuing education: 400 yuan/month for degree education (max 48 months), 3,600 yuan one-time for professional certificates',
      'Serious illness medical: Amounts over 15,000 yuan out-of-pocket within medical insurance scope, max 80,000 yuan/year',
      'Housing loan interest: 1,000 yuan/month for first home loans, max 240 months (20 years)',
      'Housing rent: 800/1,100/1,500 yuan/month depending on city size',
      'Elderly support: 3,000 yuan/month for only children, shared for others (max 1,500 each)',
      'Infant care: 2,000 yuan/month per child under 3'
    ],
    taxRateLevels: 'Seven-Level Progressive Tax Rate',
    taxRateLevelsDesc: 'China\'s personal income tax uses a seven-level progressive tax rate, from 3% to 45%. "Progressive" means only the portion exceeding each bracket\'s threshold is taxed at that rate, not all income at the highest rate. This design makes higher earners pay proportionally more tax, reflecting tax fairness.',
    yearEndBonus: 'Year-End Bonus Taxation',
    yearEndBonusDesc: 'Year-end bonuses can be taxed separately or combined with comprehensive income. When taxed separately, divide the bonus by 12 and use the monthly tax rate table. From 2024, the separate taxation policy continues, and taxpayers can choose whichever method is more favorable. Generally, combining is better for lower incomes; separate taxation is better for higher incomes.',
    taxPlanningTips: 'Tax Planning Tips',
    taxPlanningTipsItems: [
      'Fully utilize special deductions and keep documentation for verification',
      'Plan year-end bonus timing and amount to avoid "paying more tax for one extra yuan" threshold issues',
      'Spouses can negotiate how to allocate special deductions for optimal family benefit',
      'Use the personal tax app\'s refund function - annual settlement is available March-June each year',
      'Enterprise annuities and commercial health insurance can also offset tax - consult your HR'
    ],
    annualSettlement: 'Annual Tax Settlement',
    annualSettlementDesc: 'Each year from March 1 to June 30, taxpayers need to complete annual settlement for the previous year\'s comprehensive income. If prepaid tax exceeds tax due, you can apply for a refund; if prepaid tax is less than tax due, you must pay the difference. This can be done through the "Personal Income Tax" app or website.'
  },
  bmi: {
    title: 'BMI Calculator',
    subtitle: 'Calculate Body Mass Index to evaluate health status',
    height: 'Height',
    weight: 'Weight',
    heightUnit: 'cm',
    weightUnit: 'kg',
    calculate: 'Calculate BMI',
    bmiIndex: 'BMI Index',
    underweight: 'Underweight',
    normal: 'Normal',
    overweight: 'Overweight',
    obese: 'Obese',
    healthyRange: 'Healthy Weight Range',
    idealWeight: 'Ideal Weight',
    suggestion: 'Weight Suggestion',
    keepWeight: 'Maintain current weight',
    loseWeight: 'Suggest losing {n} kg',
    gainWeight: 'Suggest gaining {n} kg',
    standardTable: 'BMI Standard Reference Table (China Standard)',
    category: 'Category',
    bmiRange: 'BMI Range',
    healthRisk: 'Health Risk',
    malnutritionRisk: 'Malnutrition Risk',
    healthyWeight: 'Healthy Weight',
    increased: 'Increased',
    highRisk: 'High Risk',
    whatIsBmi: 'What is BMI?',
    whatIsBmiDesc: 'BMI (Body Mass Index) is a measure proposed by Belgian statistician Adolphe Quetelet in 1832 to assess body fatness. BMI = Weight(kg) ÷ Height(m)². Although BMI cannot directly measure body fat, it has become the most commonly used international indicator for assessing healthy weight because it is simple to calculate and easy to promote.',
    bmiLimitations: 'BMI Limitations',
    bmiLimitationsItems: [
      'Doesn\'t distinguish fat from muscle: Athletes may have high BMI but be very healthy',
      'Doesn\'t consider fat distribution: Abdominal fat (visceral fat) is more harmful to health',
      'Not suitable for special groups: Pregnant women, elderly, and children need different assessment standards',
      'Ethnic differences: Asians may have higher body fat at the same BMI, so China\'s standard is stricter than WHO'
    ],
    bmiStandards: 'BMI Standards Comparison',
    bmiStandardsDesc: 'WHO standard: Normal range 18.5-24.9. China standard is stricter: Normal range 18.5-23.9. Research shows Asians may face health risks at lower BMI levels. Chinese people are recommended to use China\'s standard.',
    otherIndicators: 'Other Health Indicators',
    otherIndicatorsItems: [
      'Waist circumference: Male < 90cm, Female < 85cm is normal (China standard)',
      'Waist-to-hip ratio (WHR): Male < 0.9, Female < 0.85 is normal',
      'Body fat percentage: Male 15-20%, Female 20-25% is ideal',
      'Visceral fat level: 1-9 is normal, 10-14 is elevated, 15+ is dangerous'
    ],
    healthyWeightTips: 'Healthy Weight Management Tips',
    healthyWeightTipsItems: [
      'Lose no more than 0.5-1 kg per week - rapid weight loss tends to rebound',
      'Combine aerobic exercise with strength training to increase muscle mass and boost metabolism',
      'Control total calorie intake but don\'t diet excessively causing malnutrition',
      'Ensure adequate sleep - lack of sleep affects metabolism and appetite regulation',
      'Monitor weight changes regularly and maintain a long-term healthy lifestyle'
    ],
    idealWeightFormulas: 'Ideal Weight Formulas',
    idealWeightFormulasDesc: 'Besides BMI, there are several ideal weight calculation methods:',
    idealWeightFormulasItems: [
      'Simple formula: Male = Height(cm) - 105, Female = Height(cm) - 110',
      'Broca formula: Ideal weight(kg) = Height(cm) - 100',
      'BMI-based: Ideal weight = 21.5 × Height(m)² (using BMI normal range midpoint)'
    ],
    bmiAndHealth: 'BMI and Health Risks',
    bmiAndHealthDesc: 'Research shows BMI correlates with various disease risks. High BMI increases risk of type 2 diabetes, cardiovascular disease, hypertension, and certain cancers; low BMI may lead to malnutrition, osteoporosis, and weakened immunity. Maintaining BMI within normal range is fundamental to good health.'
  },
  exchange: {
    title: 'Exchange Rate',
    subtitle: 'Multi-currency real-time exchange rate calculator',
    inputPlaceholder: 'Enter amount',
    resultPlaceholder: 'Conversion result',
    currentRate: 'Current Rate',
    reverseRate: 'Reverse Rate',
    quickConvert: 'Quick Convert',
    ratesTable: 'Major Currency Rates (Based on CNY)',
    currency: 'Currency',
    code: 'Code',
    toCny: 'To CNY',
    fromCny: 'From CNY',
    refreshRates: 'Refresh Rates',
    refreshing: 'Refreshing...',
    liveData: 'Live Data',
    localData: 'Local Data',
    disclaimer: 'Exchange rates are for reference only. Please check with your bank for actual transactions.',
    updateTime: 'Last Updated',
    dataSource: 'Data Source',
    currencies: {
      CNY: 'Chinese Yuan',
      USD: 'US Dollar',
      EUR: 'Euro',
      GBP: 'British Pound',
      JPY: 'Japanese Yen',
      KRW: 'Korean Won',
      HKD: 'Hong Kong Dollar',
      TWD: 'Taiwan Dollar',
      SGD: 'Singapore Dollar',
      AUD: 'Australian Dollar',
      CAD: 'Canadian Dollar',
      CHF: 'Swiss Franc',
      THB: 'Thai Baht',
      MYR: 'Malaysian Ringgit',
      RUB: 'Russian Ruble'
    },
    whatIsExchangeRate: 'What is an Exchange Rate?',
    whatIsExchangeRateDesc: 'An exchange rate is the ratio at which one currency can be exchanged for another. For example, if USD/CNY is 7.2, it means 1 US dollar can be exchanged for 7.2 Chinese yuan. Exchange rates are influenced by many factors including national economic conditions, interest rate differentials, international trade, and political stability. Rates fluctuate constantly, so understanding exchange rates is important for travel abroad, overseas shopping, and cross-border investing.',
    exchangeRateTypes: 'Types of Exchange Rates',
    exchangeRateTypesItems: [
      'Spot rate: The current market price for immediate currency exchange',
      'Forward rate: The rate agreed upon for currency exchange at a future date',
      'Bid/Ask price: The bank\'s buying and selling prices for foreign currency, with a spread between them',
      'Mid-rate: The average of bid and ask prices, typically used as a reference quote'
    ],
    factorsAffectingRates: 'Factors Affecting Exchange Rates',
    factorsAffectingRatesItems: [
      'Interest rate differentials: Higher interest rate countries\' currencies tend to appreciate, attracting international capital',
      'Inflation: High inflation countries\' currencies lose purchasing power, leading to depreciation',
      'Trade balance: Countries with trade surpluses have more foreign exchange income, stronger currency demand, favoring appreciation',
      'Political/economic stability: Stable countries\' currencies are more trusted, with stronger exchange rates',
      'Central bank intervention: Central banks can influence exchange rates through foreign exchange market operations'
    ],
    exchangeTips: 'Practical Exchange Tips',
    exchangeTipsItems: [
      'Monitor exchange rate trends before traveling abroad; exchange when rates are favorable',
      'Different banks may have different rates and fees - shop around',
      'Avoid exchanging at airports or tourist spots where rates are typically worse',
      'When using credit cards abroad, choose local currency settlement (DCC) to avoid multiple conversion losses',
      'Consider exchanging in batches for large amounts to reduce exchange rate volatility risk'
    ],
    currencySymbols: 'Common Currency Symbols',
    currencySymbolsDesc: 'Different countries use different symbols for their currencies: ¥ (CNY/JPY), $ (USD), € (EUR), £ (GBP), ₩ (KRW), etc. Using correct currency symbols when writing amounts helps avoid confusion.',
    rmbInternationalization: 'RMB Internationalization',
    rmbInternationalizationDesc: 'In recent years, RMB internationalization has progressed steadily. In 2016, the RMB was included in the IMF\'s Special Drawing Rights (SDR) currency basket. More countries and regions are accepting RMB for trade settlement. The RMB exchange rate formation mechanism continues to become more market-oriented, referencing a basket of currencies for adjustment.'
  },
  markdown: {
    title: 'Markdown Editor',
    subtitle: 'Real-time preview Markdown editor with common syntax support',
    bold: 'Bold',
    italic: 'Italic',
    heading: 'Heading',
    list: 'Unordered List',
    orderedList: 'Ordered List',
    link: 'Link',
    image: 'Image',
    inlineCode: 'Inline Code',
    codeBlock: 'Code Block',
    quote: 'Quote',
    divider: 'Divider',
    copyMarkdown: 'Copy Markdown',
    copyHtml: 'Copy HTML',
    inputLabel: 'Markdown Input',
    previewLabel: 'Preview',
    inputPlaceholder: 'Enter Markdown content here...',
    syntaxHelp: 'Markdown Syntax Reference',
    markdownCopied: 'Markdown copied!',
    htmlCopied: 'HTML copied!',
    whatIsMarkdown: 'What is Markdown?',
    whatIsMarkdownDesc: 'Markdown is a lightweight markup language created by John Gruber in 2004. Its design philosophy is to let people write documents in simple, readable plain text format that can then be converted into structured HTML. Markdown syntax is concise and intuitive with a low learning curve, making it the preferred writing format for programmers, technical writers, and content creators.',
    markdownAdvantages: 'Advantages of Markdown',
    markdownAdvantagesItems: [
      'Simple syntax: Learn basic syntax in minutes, very quick to pick up',
      'Focus on content: No need to worry about formatting, just concentrate on writing',
      'Cross-platform: Plain text format opens in any editor',
      'Version control friendly: Works well with Git for collaboration and change tracking',
      'Widely supported: GitHub, GitLab, Notion, and many platforms support it'
    ],
    markdownSyntaxGuide: 'Common Syntax Overview',
    markdownSyntaxGuideItems: [
      '# Heading 1, ## Heading 2, ### Heading 3',
      '**bold** or __bold__',
      '*italic* or _italic_',
      '[link text](URL)',
      '![image description](image URL)',
      '- unordered list or * unordered list',
      '1. ordered list',
      '> quoted text',
      '`inline code`',
      '```code block```',
      '--- or *** divider'
    ],
    markdownUseCases: 'Markdown Use Cases',
    markdownUseCasesItems: [
      'Technical documentation: README, API docs, development guides',
      'Blog writing: Most blogging platforms support Markdown',
      'Note-taking: Preferred format for Notion, Obsidian, and similar apps',
      'E-book creation: GitBook, mdBook, and similar tools',
      'Forum posts: Stack Overflow, dev communities'
    ],
    markdownTips: 'Markdown Writing Tips',
    markdownTipsItems: [
      'Use heading levels appropriately to keep document structure clear',
      'Remember to specify language type in code blocks for syntax highlighting',
      'Use lists to organize parallel information for better readability',
      'Manage images using image hosting services or relative paths',
      'Use online table generators for complex tables'
    ]
  },
  traditional: {
    title: 'Chinese Converter',
    subtitle: 'Convert between Simplified and Traditional Chinese',
    toTraditional: 'To Traditional',
    toSimplified: 'To Simplified',
    swap: 'Swap',
    inputLabel: 'Input Text',
    outputLabel: 'Conversion Result',
    inputPlaceholder: 'Enter Chinese text to convert...',
    outputPlaceholder: 'Converted result will be displayed here...',
    emptyError: 'Please enter text to convert',
    toTraditionalSuccess: 'Converted to Traditional Chinese',
    toSimplifiedSuccess: 'Converted to Simplified Chinese',
    aboutTitle: 'About Traditional Chinese',
    aboutDesc1: 'Traditional Chinese characters, also known as orthodox or classical Chinese characters, were the standard Chinese characters used in China before the 1956 simplified character scheme. Traditional characters have over 3,000 years of history and carry the cultural essence of the Chinese nation.',
    aboutUsage: 'Usage Areas: Traditional characters are mainly used in Taiwan, Hong Kong, and Macau; Simplified characters are mainly used in mainland China and Southeast Asian Chinese communities.',
    aboutValue: 'Cultural Value: Traditional characters preserve the original structure and etymology of Chinese characters, making them important for studying character evolution, calligraphy art, and reading ancient texts.',
    simplificationHistory: 'History of Chinese Character Simplification',
    simplificationHistoryDesc: 'In 1956, the State Council of the People\'s Republic of China announced the "Scheme for Simplifying Chinese Characters" and began promoting simplified characters. The purpose was to reduce illiteracy and make writing and printing easier. Simplification methods included: reducing strokes, adapting cursive script forms, using simple symbols for complex components, and merging homophone characters. After several adjustments, there are now over 2,000 simplified characters in use.',
    conversionChallenges: 'Challenges in Simplified-Traditional Conversion',
    conversionChallengesItems: [
      'One-to-many mapping: One simplified character may correspond to multiple traditional characters, e.g., "发" maps to "發" and "髮"',
      'Regional vocabulary differences: Different regions use different terms, e.g., "软件" vs "軟體" (software), "信息" vs "資訊" (information)',
      'Variant character issues: Same character may have multiple forms, need to choose based on regional conventions',
      'Proper nouns: Names of people and places need special attention during conversion'
    ],
    traditionalUseCases: 'Applications for Conversion',
    traditionalUseCasesItems: [
      'Cross-strait business communication: Documents for cross-regional business exchanges',
      'Academic research: Reading ancient texts and Hong Kong/Taiwan academic literature',
      'Cultural creation: Calligraphy, seal carving, traditional culture content creation',
      'Film subtitles: Subtitle production for different regional audiences',
      'Website localization: Content adaptation for different regional users'
    ],
    learningBenefits: 'Benefits of Learning Traditional Characters',
    learningBenefitsItems: [
      'Better understand the principles and evolution of Chinese character formation',
      'Read original ancient texts and directly experience traditional culture',
      'Appreciate and learn traditional calligraphy art',
      'Communicate more smoothly with Hong Kong and Taiwan compatriots',
      'Enhance Chinese literacy and enrich language expression'
    ],
    commonDifferences: 'Common Simplified-Traditional Differences',
    commonDifferencesItems: [
      '龙/龍, 凤/鳳, 云/雲, 风/風',
      '爱/愛, 义/義, 国/國, 学/學',
      '门/門, 开/開, 关/關, 间/間',
      '车/車, 马/馬, 鱼/魚, 鸟/鳥',
      '书/書, 画/畫, 乐/樂, 体/體'
    ]
  },
  about: {
    title: 'About MoonRise Tools',
    subtitle: 'Practical online utility tool collection',
    mission: 'Our Mission',
    missionDesc: 'MoonRise Tools is dedicated to providing simple, efficient, and free online tools to help users solve various problems in daily work and life. We believe that good tools should be simple to use, require no download or installation, and be ready to use immediately.',
    features: 'Features',
    featureFree: 'Completely Free',
    featureFreeDesc: 'All tools are free forever, no registration required',
    featureSecure: 'Privacy Safe',
    featureSecureDesc: 'Data processed locally, not uploaded to servers',
    featureFast: 'Fast & Efficient',
    featureFastDesc: 'Ready to use instantly, no loading wait',
    featureCross: 'Cross-platform',
    featureCrossDesc: 'Supports computers, tablets, and mobile devices',
    featureDark: 'Dark Mode',
    featureDarkDesc: 'Supports light/dark theme switching',
    featureUpdate: 'Continuous Updates',
    featureUpdateDesc: 'Constantly adding new tools and optimizing experience',
    toolList: 'Tool List',
    contact: 'Contact Us',
    contactDesc: 'If you have any suggestions, feedback, or cooperation intentions, please feel free to contact us. We value every user\'s opinion, and your feedback will help us continuously improve our tools.',
    website: 'Website',
    email: 'Email',
    contactPromise: 'We promise to reply to your feedback as soon as possible. Thank you for using MoonRise Tools!',
    privacyCommitment: 'Privacy Commitment',
    privacyDesc: 'MoonRise Tools values your privacy. All our tools run locally in your browser. Your input data is never uploaded to any server. We do not collect, store, or share your personal data.',
    privacyLink: 'For detailed privacy protection information, please see our',
    privacyPage: 'Privacy Policy',
    faq: 'FAQ',
    faqFree: 'Is the website free?',
    faqFreeAnswer: 'Yes, all tools on MoonRise Tools are completely free with no hidden charges.',
    faqRegister: 'Do I need to register?',
    faqRegisterAnswer: 'No. You can use all tools directly without registering or logging in.',
    faqSuggestion: 'Can I suggest new tools?',
    faqSuggestionAnswer: 'Absolutely! We welcome you to email us with suggestions for new tools. We will carefully consider every suggestion.',
    story: 'Our Story',
    storyDesc: 'MoonRise Tools was born from a simple idea. As a developer, I frequently needed various small utilities in my daily work — formatting JSON, converting timestamps, calculating mortgage payments. But each time, I either had to download and install software, or deal with online tools full of ads that required registration. I thought: why can\'t there be a clean, free, no-registration-required utility website? So MoonRise Tools officially launched in early 2026.',
    storyDesc2: 'The name "MoonRise" symbolizes "the moon rising to illuminate the night" — we hope these small tools can silently provide help when you need them, like moonlight illuminating your work and life.',
    milestones: 'Development Timeline',
    milestonesItems: [
      'January 2026: Website launched with initial tools including JSON Formatter, Timestamp Converter, Word Count, Calculator',
      'January 2026: Added Markdown Editor, Image Processing, Chinese Converter, Amount Converter tools',
      'February 2026: Launched Mortgage Calculator, Tax Calculator, BMI Calculator, Exchange Rate Converter',
      'February 2026: Added bilingual support (Chinese/English), Dark Mode, and Tech Blog with original content',
      'Ongoing: More practical tools are being developed, stay tuned...'
    ],
    techStack: 'Technology Stack',
    techStackDesc: 'MoonRise Tools is built with modern frontend technologies, ensuring excellent performance and user experience:',
    techStackItems: [
      'Frontend Framework: Vue.js 3 with component-based development and reactive data binding',
      'Routing: Vue Router for smooth single-page application navigation',
      'Internationalization: vue-i18n for seamless Chinese/English switching',
      'Styling: CSS variables + responsive design for perfect adaptation across screen sizes',
      'Deployment: Static file deployment with global CDN acceleration for fast access',
      'Privacy by Design: All data processing done locally in the browser, zero data uploads'
    ]
  },
  image: {
    title: 'Image Processing',
    subtitle: 'Image compression, cropping, and filters - all processed locally for privacy',
    uploadHint: 'Click or drag to upload image',
    supportedFormats: 'Supports JPG, PNG, GIF, WebP formats',
    basicAdjust: 'Basic Adjustments',
    quality: 'Quality',
    scale: 'Scale',
    filterEffects: 'Filter Effects',
    imageInfo: 'Image Info',
    originalSize: 'Original Size',
    outputSize: 'Output Size',
    originalFileSize: 'Original File Size',
    estimatedSize: 'Estimated Size',
    download: 'Download',
    reset: 'Reset',
    clear: 'Clear',
    filters: {
      none: 'Original',
      grayscale: 'Grayscale',
      sepia: 'Sepia',
      invert: 'Invert',
      blur: 'Blur',
      brightness: 'Brightness',
      contrast: 'Contrast',
      saturate: 'Saturate'
    },
    whyCompress: 'Why Compress Images?',
    whyCompressDesc: 'In the internet age, images are one of the most important content forms on web pages and applications. However, high-resolution images are often large, affecting page load speed, consuming user bandwidth, and taking up storage space. Properly compressing images can significantly reduce file size while maintaining visual quality, improving user experience.',
    formatGuide: 'Image Format Guide',
    formatGuideItems: [
      'JPEG/JPG: Best for photos and complex images, supports lossy compression, no transparency support',
      'PNG: Supports transparency and lossless compression, ideal for icons, logos, and screenshots',
      'GIF: Supports animation and transparency, limited to 256 colors, good for simple animations',
      'WebP: Modern format by Google, 25-34% smaller than JPEG at same quality',
      'SVG: Vector format, scales infinitely without quality loss, ideal for icons and simple graphics'
    ],
    compressionTips: 'Image Compression Tips',
    compressionTipsItems: [
      'Use JPEG for photos, 70-85% quality is usually sufficient',
      'Use PNG or WebP for images needing transparency',
      'Size reduction is more effective than quality reduction - resize based on actual display dimensions',
      'Process images uniformly before batch uploading to websites',
      'Mobile-displayed images can use lower resolution'
    ],
    privacyNote: 'Privacy Protection',
    privacyNoteDesc: 'All image processing in this tool is done locally in your browser. Your uploaded images are never sent to any server. After processing, image data only exists on your device and is cleared when you close the page. You can safely process images containing sensitive information.',
    webOptimization: 'Web Image Optimization Tips',
    webOptimizationItems: [
      'Prioritize loading above-the-fold images; use lazy loading for below-the-fold images',
      'Provide different resolution images based on device pixel ratio (responsive images)',
      'Use CDN for faster image delivery and enable caching',
      'Consider WebP format with JPEG fallback for unsupported browsers',
      'Keep compressed images under 200KB when possible'
    ]
  },
  uppercase: {
    title: 'Amount Converter',
    subtitle: 'Convert Arabic numerals to Chinese uppercase amount',
    inputLabel: 'Enter Amount (Number)',
    inputPlaceholder: 'Enter amount, e.g.: 12345.67',
    outputLabel: 'Uppercase Amount',
    waiting: 'Waiting for input...',
    quickExamples: 'Quick Examples',
    digitTable: 'Chinese Uppercase Digit Table',
    arabicDigit: 'Arabic Digit',
    chineseUppercase: 'Chinese Uppercase',
    unitInfo: 'Unit Uppercase',
    unitValues: 'Shi, Bai, Qian, Wan, Yi',
    amountUnit: 'Amount Unit',
    amountUnitValues: 'Yuan, Jiao, Fen, Zheng',
    usageTips: 'Usage Tips',
    tips: [
      'Maximum amount: 9,999,999,999,999.99 (about 10 trillion)',
      'Supports up to 2 decimal places',
      'Commonly used for checks, bank documents, financial vouchers',
      'Uppercase amounts effectively prevent tampering'
    ],
    invalidFormat: 'Please enter a valid amount format',
    negativeError: 'Amount cannot be negative',
    overflowError: 'Amount exceeds conversion range',
    zero: 'Zero Yuan Zheng',
    whyUppercase: 'Why Use Uppercase Amounts?',
    whyUppercaseDesc: 'Uppercase amounts are an important requirement in Chinese financial regulations, primarily to prevent amount tampering. Arabic numerals can easily be altered (like changing 1 to 7, or 100 to 1000), while Chinese uppercase numerals have complex structures that are difficult to forge or modify. Therefore, important financial documents like checks, contracts, and IOUs require both Arabic and uppercase amounts.',
    uppercaseHistory: 'History of Uppercase Numerals',
    uppercaseHistoryDesc: 'China\'s use of uppercase numerals for fraud prevention dates back to the Ming Dynasty. Legend has it that Emperor Zhu Yuanzhang, after corruption cases occurred, ordered the use of uppercase numerals in official documents to prevent officials from altering numbers for embezzlement. This system has continued to this day, becoming an important tradition in Chinese financial management.',
    writingRules: 'Uppercase Amount Writing Rules',
    writingRulesItems: [
      'Chinese uppercase amounts should be preceded by "人民币" (Renminbi)',
      'Uppercase digits should immediately follow "人民币" with no blank space',
      'When there\'s a 0 in Arabic numerals, write "零" (zero) in uppercase',
      'If amount ends at Yuan with no Jiao/Fen, add "整" (whole) after Yuan',
      'If amount has Jiao but no Fen, add "整" after Jiao'
    ],
    commonMistakes: 'Common Writing Errors',
    commonMistakesItems: [
      'Using simplified characters: Should use "壹贰叁" not "一二三"',
      'Missing units: Each digit must have its corresponding unit',
      'Zero handling: Consecutive zeros write only one "零"',
      'Writing "元": Can write "元" or "圆", but be consistent',
      'Ending treatment: Add "整" after Yuan if no Jiao/Fen'
    ],
    uppercaseExamples: 'Practical Examples',
    uppercaseExamplesItems: [
      '¥1,234.56 → 人民币壹仟贰佰叁拾肆元伍角陆分',
      '¥10,000.00 → 人民币壹万元整',
      '¥305.40 → 人民币叁佰零伍元肆角整',
      '¥1,000,000.00 → 人民币壹佰万元整'
    ]
  },
  privacy: {
    title: 'Privacy Policy',
    lastUpdate: 'Last Updated: February 5, 2026',
    overview: 'Overview',
    overviewDesc: 'Welcome to MoonRise Tools (ysgjw.com). We take your privacy very seriously. This privacy policy explains how we collect, use, and protect your information.',
    collection: 'Information Collection',
    autoCollection: 'Automatically Collected Information',
    autoCollectionDesc: 'When you visit this website, we may automatically collect the following information:',
    autoCollectionItems: [
      'Device information (browser type, operating system)',
      'Access time and date',
      'Pages visited and features used',
      'IP address (for approximate geographic location)',
      'Referral source (which website you came from)'
    ],
    userProvided: 'Information You Provide',
    userProvidedDesc: 'All tools on this website run locally in your browser. We do not collect any data you input, including but not limited to:',
    userProvidedItems: ['JSON data', 'Text content', 'Calculation values', 'Uploaded images'],
    dataControl: 'Your data security is in your control. All processing is done locally.',
    cookies: 'Cookies and Tracking Technologies',
    cookiesDesc: 'We use the following technologies to improve user experience:',
    googleAnalytics: 'Google Analytics',
    googleAnalyticsDesc: 'We use Google Analytics to analyze website traffic and user behavior to help us improve the website. Google Analytics uses cookies to collect anonymous usage data. You can disable cookies through browser settings or install the',
    gaOptout: 'Google Analytics opt-out add-on',
    localStorage: 'Local Storage',
    localStorageDesc: 'We may use browser local storage (localStorage) to save your preferences, such as theme mode (light/dark). This data is only stored on your device.',
    advertising: 'Advertising',
    advertisingDesc: 'This website may display third-party advertisements (such as Google AdSense). These advertising providers may use cookies to display ads related to your interests. You can visit',
    adSettings: 'Google Ads Settings',
    adSettingsLink: 'to manage your ad preferences.',
    sharing: 'Information Sharing',
    sharingDesc: 'We will not sell, trade, or otherwise transfer your personal information to third parties, unless:',
    sharingItems: ['We have your explicit consent', 'Required by law or government authorities', 'To protect our rights, property, or safety'],
    security: 'Data Security',
    securityDesc: 'We take reasonable technical and organizational measures to protect your information:',
    securityItems: ['Using HTTPS encrypted transmission', 'Regular website security updates and maintenance', 'Limiting access to user data'],
    children: "Children's Privacy",
    childrenDesc: 'This website is not intended for children under 13. We do not knowingly collect personal information from children.',
    rights: 'Your Rights',
    rightsDesc: 'You have the right to:',
    rightsItems: ['Know what information we collect', 'Request deletion of your information', 'Opt out of data collection (by disabling cookies)', 'Ask questions about this privacy policy'],
    updates: 'Privacy Policy Updates',
    updatesDesc: 'We may update this privacy policy from time to time. Updated policies will be posted on this page with an updated "Last Updated" date. We recommend checking this page periodically.',
    contact: 'Contact Us',
    contactDesc: 'If you have any questions about this privacy policy, please contact us through:'
  },
  textDedup: {
    title: 'Text Deduplication',
    subtitle: 'Remove duplicate content with multiple split modes: by line, comma, sentence, and more',
    dedup: 'Deduplicate',
    inputLabel: 'Input Text',
    outputLabel: 'Result',
    inputPlaceholder: 'Enter or paste text to deduplicate...',
    outputPlaceholder: 'Deduplicated result will appear here...',
    trimLines: 'Trim whitespace',
    ignoreCase: 'Ignore case',
    ignoreEmpty: 'Ignore empty items',
    splitMode: 'Split by:',
    splitByLine: 'Line',
    splitByComma: 'Comma',
    splitBySentence: 'Sentence',
    splitByCustom: 'Custom',
    customSepPlaceholder: 'Enter separator',
    success: 'Deduplication complete!',
    statsInfo: 'Total {total} items, removed {removed} duplicates, {remain} remaining',
    whatIs: 'What is Text Deduplication?',
    whatIsDesc: 'Text deduplication removes duplicate lines from text content. In daily work, we often encounter data exported from databases, log files, email lists, etc. that contain many duplicate entries. Manually deleting these duplicates is time-consuming and error-prone. This deduplication tool can clean up duplicates with one click, greatly improving work efficiency. All processing is done locally in your browser — your data is never uploaded to any server.',
    useCasesTitle: 'Applications',
    useCases: [
      'Data cleaning: Remove duplicate records from database or Excel exports',
      'Email list management: Deduplicate email addresses or contact information',
      'Log analysis: Filter duplicate log entries for faster problem diagnosis',
      'Keyword management: Deduplicate SEO keyword lists to avoid redundant campaigns',
      'Development: Clean up duplicate configuration items, URLs, or IP addresses'
    ],
    featuresTitle: 'Features',
    features: [
      'Line-by-line deduplication with exact content matching',
      'Optional case-insensitive mode for flexible English text handling',
      'Automatic empty line filtering for clean results',
      'Real-time statistics showing removal count',
      'All processing done locally in browser for data privacy'
    ],
    tipsTitle: 'Tips',
    tips: [
      'Enable "Ignore empty lines" when processing large datasets to reduce noise',
      'Enable "Ignore case" for English data to catch duplicates differing only in capitalization',
      'Combine with the Text Sort tool to organize results after deduplication',
      'The tool preserves the first occurrence of each duplicate by default'
    ]
  },
  textReplace: {
    title: 'Text Replace',
    subtitle: 'Online text find and replace with regex support',
    findLabel: 'Find',
    replaceLabel: 'Replace with',
    findPlaceholder: 'Enter text to find...',
    replacePlaceholder: 'Enter replacement text (leave empty to delete matches)',
    replace: 'Replace',
    ignoreCase: 'Ignore case',
    useRegex: 'Use regex',
    replaceAll: 'Replace all',
    inputLabel: 'Input Text',
    outputLabel: 'Result',
    inputPlaceholder: 'Enter or paste text to process...',
    outputPlaceholder: 'Replaced result will appear here...',
    emptyFind: 'Please enter text to find',
    success: 'Replace complete!',
    regexError: 'Regex error',
    countInfo: '{n} replacements made',
    whatIs: 'What is Text Replace?',
    whatIsDesc: 'Text replacement is one of the most fundamental and commonly used text editing features. Whether you need to modify specific words in a document, batch-update variable names in code, or clean up specific characters, this tool gets it done quickly. It supports both simple text matching and powerful regular expressions for complex text processing needs. All operations are performed locally in your browser to protect your data.',
    useCasesTitle: 'Applications',
    useCases: [
      'Batch document editing: Replace outdated product names, contact info, etc.',
      'Code refactoring: Batch rename variables, functions, or API endpoints',
      'Data format conversion: Replace comma delimiters with tabs, or vice versa',
      'Character cleanup: Remove extra spaces, line breaks, or invisible characters',
      'Content moderation: Replace sensitive words with asterisks or alternatives'
    ],
    regexTitle: 'Regex Basics',
    regexDesc: 'Regular expressions are powerful text matching patterns for complex find-and-replace operations. Here are some common examples:',
    regexExamples: [
      '\\d+ matches one or more digits, e.g., "123" in "abc123"',
      '\\s+ matches one or more whitespace characters, useful for collapsing multiple spaces',
      '^\\s+ matches leading spaces, useful for removing indentation',
      '\\s+$ matches trailing spaces, useful for trimming line endings',
      '<[^>]+> matches HTML tags, useful for stripping HTML markup',
      '(\\d{4})-(\\d{2})-(\\d{2}) matches date format, use $3/$2/$1 to rearrange'
    ]
  },
  textSort: {
    title: 'Text Sort',
    subtitle: 'Sort, reverse, or shuffle text lines',
    sortAsc: 'Sort A→Z',
    sortDesc: 'Sort Z→A',
    reverse: 'Reverse',
    shuffle: 'Shuffle',
    ignoreCase: 'Ignore case',
    numericSort: 'Numeric sort',
    removeEmpty: 'Remove empty lines',
    inputLabel: 'Input Text',
    outputLabel: 'Sorted Result',
    inputPlaceholder: 'Enter or paste text with one item per line...',
    outputPlaceholder: 'Sorted result will appear here...',
    success: 'Sort complete!',
    whatIs: 'What is Text Sort?',
    whatIsDesc: 'The text sort tool organizes text content by line. It supports ascending (A-Z), descending (Z-A) sorting, line reversal, and random shuffling. For Chinese text, it sorts by pinyin order; for numeric content, enable numeric sort mode. This tool is highly useful for data organization, list sorting, and code formatting. All processing is done locally in your browser.',
    useCasesTitle: 'Applications',
    useCases: [
      'Alphabetical sorting: Sort name lists or glossaries alphabetically',
      'Data organization: Arrange numeric data from smallest to largest or vice versa',
      'Code formatting: Sort import statements or CSS properties alphabetically',
      'Reverse text: Move the last line to first for reverse-chronological log viewing',
      'Random ordering: Shuffle options for drawings, randomized lists, etc.',
      'Sort after dedup: Combine with dedup tool for clean, organized lists'
    ],
    featuresTitle: 'Features',
    features: [
      'Four sorting modes: ascending, descending, reverse, and random',
      'Chinese sorted by pinyin, English sorted alphabetically',
      'Numeric sort mode: correctly places "2" before "10"',
      'Optional case-insensitive mode groups "apple" with "Apple"',
      'Automatic empty line removal for clean output'
    ]
  },
  textNumber: {
    title: 'Line Numbering',
    subtitle: 'Automatically add or remove line numbers',
    startNum: 'Start number',
    separator: 'Separator',
    position: 'Position',
    before: 'Before line',
    after: 'After line',
    tab: 'Tab',
    skipEmpty: 'Skip empty lines',
    padZero: 'Zero-pad numbers',
    addNumber: 'Add Numbers',
    removeNumber: 'Remove Numbers',
    inputLabel: 'Input Text',
    outputLabel: 'Result',
    inputPlaceholder: 'Enter or paste text, one item per line...',
    outputPlaceholder: 'Numbered result will appear here...',
    success: 'Numbers added!',
    removeSuccess: 'Numbers removed!',
    whatIs: 'What is Line Numbering?',
    whatIsDesc: 'The line numbering tool automatically adds sequential numbers to each line of text. Whether organizing study notes, creating numbered lists, or processing data files, it gets the job done quickly. It supports multiple numbering formats (e.g., "1.", "1)", "1."), customizable start numbers, and positioning numbers at the beginning or end of lines. It also supports one-click number removal for easy re-editing.',
    useCasesTitle: 'Applications',
    useCases: [
      'Note organization: Add numbers to study notes or meeting minutes',
      'List creation: Quickly number to-do items or shopping lists',
      'Document editing: Add clause numbers to contracts or regulations',
      'Data processing: Add row numbers to CSV data for reference',
      'Code review: Add line numbers to code snippets for discussion'
    ]
  },
  text2html: {
    title: 'Text to HTML',
    subtitle: 'Convert plain text to HTML format code',
    modeParagraph: 'Paragraphs <p>',
    modeList: 'List <ul>',
    modeBr: 'Line breaks <br>',
    modeTable: 'Table <table>',
    inputLabel: 'Input Plain Text',
    outputLabel: 'HTML Code',
    inputPlaceholder: 'Enter plain text, one paragraph per line...\n\nIn table mode, use commas or tabs to separate columns',
    outputPlaceholder: 'Converted HTML code will appear here...',
    preview: 'HTML Preview',
    success: 'Conversion complete!',
    whatIs: 'What is Text to HTML?',
    whatIsDesc: 'The Text to HTML tool quickly converts plain text content into HTML formatted code. In web development, blog editing, and email composition, you often need to convert plain text to HTML. Manually adding HTML tags is tedious and error-prone. This tool converts text with one click, supporting paragraph, list, line break, and table conversion modes to meet different formatting needs.',
    useCasesTitle: 'Applications',
    useCases: [
      'Blog editing: Convert plain text articles to HTML format for publishing',
      'Email composition: Create HTML-formatted email content',
      'Web development: Quickly generate HTML structure code',
      'Data display: Convert CSV data to HTML tables',
      'Document conversion: Convert plain text lists to HTML ordered or unordered lists'
    ],
    modesTitle: 'Conversion Modes',
    modesDesc: [
      'Paragraph mode: Each line becomes a paragraph wrapped in <p> tags',
      'List mode: Each line becomes an <li> item wrapped in a <ul> container',
      'Line break mode: Lines are connected with <br> tags for simple line breaks',
      'Table mode: Comma or tab-separated data converts to an HTML table with the first row as header'
    ]
  },
  wordFreq: {
    title: 'Word Frequency',
    subtitle: 'Analyze word frequency distribution in text',
    analyze: 'Analyze',
    ignoreCase: 'Ignore case',
    ignorePunctuation: 'Ignore punctuation',
    skipSingleChinese: 'Skip single Chinese characters',
    skipPureNumbers: 'Skip pure numbers',
    skipEnglishPrepositions: 'Skip English prepositions/function words',
    topN: 'Show top',
    inputLabel: 'Input Text',
    inputPlaceholder: 'Enter or paste text for frequency analysis...',
    resultTitle: 'Frequency Results',
    totalWords: '{n} total words',
    word: 'Word',
    count: 'Count',
    percent: 'Ratio',
    bar: 'Distribution',
    success: 'Analysis complete!',
    whatIs: 'What is Word Frequency Analysis?',
    whatIsDesc: 'Word frequency analysis is one of the most fundamental and important methods in text analysis. By counting the occurrence of each word, you can quickly understand the theme and key information of a text. It is widely used in linguistics research, natural language processing, SEO optimization, and content analysis. This tool supports mixed Chinese-English text analysis with automatic Chinese word segmentation and English tokenization, displaying results visually.',
    useCasesTitle: 'Applications',
    useCases: [
      'SEO optimization: Analyze keyword density and distribution in web content',
      'Content analysis: Identify core themes and high-frequency vocabulary in articles',
      'Academic research: Study word frequency distribution patterns in text corpora',
      'Competitive analysis: Compare competitors\' content strategies and keyword usage',
      'Writing improvement: Check for overuse of certain words to improve article quality',
      'Language learning: Identify high-frequency words in foreign language texts for study'
    ],
    tipsTitle: 'Tips',
    tips: [
      'Enable "Ignore case" to merge "Hello" and "hello" in counting',
      'Enable "Skip single Chinese characters" to filter out single characters and keep only compound words',
      'Enable "Skip pure numbers" to filter out numeric values from the text',
      'Enable "Skip English prepositions" to filter out common function words like the, in, of',
      'Chinese text is automatically segmented into words using built-in word detection',
      'Increase "Show top" to view statistics for more low-frequency words'
    ]
  },
  namingConverter: {
    title: 'Naming Converter',
    subtitle: 'Convert between different programming naming conventions quickly',
    inputLabel: 'Enter Identifier Name',
    inputPlaceholder: 'Enter a name in any format, e.g. myVariableName, my_variable_name, my-variable-name...',
    whatIs: 'What is a Naming Converter?',
    whatIsDesc: 'Different programming languages and scenarios use different naming conventions. A naming converter helps you quickly switch between various naming formats, avoiding the tedium and errors of manual conversion. Simply enter a name in any format, and all other format variants are generated automatically. All conversions are done locally in your browser.',
    stylesTitle: 'Naming Convention Guide',
    styles: [
      'camelCase: Standard for JavaScript variables and functions, e.g. getUserName',
      'PascalCase: Used for class names and components, e.g. UserProfile',
      'snake_case: Standard for Python, Ruby, and database fields, e.g. user_name',
      'kebab-case: Standard for HTML attributes, CSS classes, and URLs, e.g. user-profile',
      'CONSTANT_CASE: Uppercase with underscores for constants, e.g. MAX_RETRY_COUNT',
      'dot.case: Used for config files and package names, e.g. com.example.app',
      'path/case: Used for file paths, e.g. user/profile/avatar',
      'Title Case: Each word capitalized, e.g. User Profile Name',
      'Sentence case: Only first letter capitalized, e.g. User profile name'
    ],
    tipsTitle: 'Tips',
    tips: [
      'Automatically detects camelCase, PascalCase, snake_case, kebab-case formats',
      'Also supports space-separated text input, which will be split into words',
      'Click the copy button next to each format to quickly copy the result'
    ]
  },
  regexTester: {
    title: 'Regex Tester',
    subtitle: 'Test and debug regular expressions online with real-time highlighting',
    patternPlaceholder: 'Enter regex pattern...',
    testString: 'Test String',
    testPlaceholder: 'Enter text to match against...',
    matchResult: 'Match Result',
    matchCount: '{n} matches found',
    matchDetails: 'Match Details',
    matchValue: 'Match',
    matchPos: 'Position',
    matchGroups: 'Groups',
    quickRef: 'Quick Reference (click to insert)',
    ref: {
      dot: 'Any character',
      digit: 'Digit [0-9]',
      word: 'Word char [a-zA-Z0-9_]',
      space: 'Whitespace',
      start: 'Start of line',
      end: 'End of line',
      star: '0 or more',
      plus: '1 or more',
      question: '0 or 1',
      charset: 'Character set',
      group: 'Capture group',
      or: 'Alternation'
    },
    whatIs: 'What is Regex?',
    whatIsDesc: 'Regular Expressions (Regex) are a powerful tool for describing text matching patterns. They can search, validate, replace, and extract specific content from text. Regex is widely used in programming languages, text editors, and command-line tools. This tool supports real-time regex testing with highlighted matches and detailed capture group information. All processing is done locally in your browser.',
    useCasesTitle: 'Applications',
    useCases: [
      'Form validation: Verify email, phone number, ID card format',
      'Data extraction: Extract specific patterns from logs, web pages, or text',
      'Text replacement: Batch replace text matching specific patterns',
      'Log analysis: Filter key information from massive log files',
      'Code refactoring: Use regex search-replace for batch code modifications',
      'Web scraping: Extract data from HTML pages'
    ]
  },
  colorConverter: {
    title: 'Color Converter',
    subtitle: 'Convert between HEX, RGB, HSL color formats with live preview',
    pickerLabel: 'Use color picker:',
    whatIs: 'What is Color Conversion?',
    whatIsDesc: 'In frontend development and design, colors can be represented in multiple formats: HEX (hexadecimal), RGB (Red Green Blue), HSL (Hue Saturation Lightness), and more. Different scenarios and tools may require different color formats. The color converter helps you quickly convert between formats with live color preview. All processing is done locally in your browser.',
    formatsTitle: 'Color Format Guide',
    formats: [
      'HEX: e.g. #FF5733, the most common CSS color format using # followed by 6 hex digits',
      'RGB: e.g. rgb(255, 87, 51), uses 0-255 values for Red, Green, Blue channels',
      'RGBA: Adds an Alpha (opacity) channel to RGB, e.g. rgba(255, 87, 51, 0.8)',
      'HSL: e.g. hsl(9, 100%, 60%), more intuitive representation using Hue, Saturation, Lightness',
      'HSLA: Adds an Alpha (opacity) channel to HSL'
    ],
    tipsTitle: 'Tips',
    tips: [
      'Edit any format input and all others will sync automatically',
      'Use the color picker to select colors visually',
      'Click the copy button to quickly copy CSS color values',
      'HEX supports 3-character shorthand (e.g. #F00) auto-expanded to 6 characters'
    ]
  },
  codeFormatter: {
    title: 'Code Formatter',
    subtitle: 'Format and compress CSS, JavaScript, HTML, SQL code online',
    format: 'Format',
    compress: 'Compress',
    inputLabel: 'Input Code',
    outputLabel: 'Output',
    outputPlaceholder: 'Formatted/compressed result will appear here...',
    formatSuccess: 'Format successful!',
    compressSuccess: 'Compression successful!',
    formatError: 'Format error',
    whatIs: 'What is Code Formatting?',
    whatIsDesc: 'Code formatting (also known as code beautification) converts compressed or messy code into a clearly structured, properly indented format for easier reading and maintenance. Code compression does the reverse, removing unnecessary whitespace and line breaks to reduce file size and optimize loading performance. This tool supports formatting and compression for CSS, JavaScript, HTML, and SQL. All operations are done locally in your browser.',
    featuresTitle: 'Features',
    features: [
      'CSS format/compress: Beautify styles or remove comments and whitespace to reduce file size',
      'JavaScript format/compress: Auto-indent JS code, or remove whitespace to minimize',
      'HTML format/compress: Organize HTML tag indentation, or merge into single line',
      'SQL format/compress: Uppercase SQL keywords with proper line breaks, or compress to single line',
      'Switch between languages with one click in the same interface'
    ]
  },
  crontab: {
    title: 'Crontab Parser',
    subtitle: 'Parse Crontab expressions and preview execution times',
    expressionLabel: 'Crontab Expression',
    presets: 'Common Presets',
    nextRunsLabel: 'Next 10 Execution Times',
    errorFormat: 'Invalid Crontab format. Need 5 fields (minute hour day month weekday)',
    field: {
      minute: 'Minute',
      hour: 'Hour',
      day: 'Day',
      month: 'Month',
      weekday: 'Weekday'
    },
    week: {
      sun: 'Sun', mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat'
    },
    preset: {
      everyMinute: 'Every minute',
      everyHour: 'Every hour on the hour',
      everyDay: 'Every day at midnight',
      weekday9am: 'Weekdays at 9 AM',
      everySunday: 'Every Sunday midnight',
      everyMonth: '1st of every month',
      every5min: 'Every 5 minutes',
      every2hours: 'Every 2 hours',
      everyMonday: 'Every Monday midnight',
      everyYear: 'January 1st every year'
    },
    desc: {
      everyMinute: 'Runs every minute',
      everyNMin: 'Every {n} minutes',
      atMinute: 'At minute {n}',
      everyNHour: 'Every {n} hours',
      atHour: 'At {n} o\'clock',
      everyNDay: 'Every {n} days',
      onDay: 'On day {n}',
      inMonth: 'In month {n}',
      onWeekday: 'On weekday {n}'
    },
    whatIs: 'What is Crontab?',
    whatIsDesc: 'Crontab is a Unix/Linux utility for scheduling periodic tasks (commands). A Crontab expression consists of 5 fields representing minute, hour, day, month, and weekday, separated by spaces. By combining these fields, you can flexibly define various execution schedules. This tool helps you parse and validate Crontab expressions and preview future execution times.',
    syntaxTitle: 'Crontab Syntax',
    syntaxItems: [
      'Format: minute(0-59) hour(0-23) day(1-31) month(1-12) weekday(0-7, both 0 and 7 = Sunday)',
      '* means any value (matches every unit)',
      '*/n means every n units, e.g. */5 means every 5 minutes',
      'a-b means range, e.g. 1-5 means Monday to Friday',
      'a,b,c means list, e.g. 1,15 means 1st and 15th of month',
      'Can be combined, e.g. 0 9-17 * * 1-5 means weekdays every hour (9-17)'
    ],
    tipsTitle: 'Tips',
    tips: [
      'Click presets to quickly fill in expressions',
      'Editing the expression shows real-time parsing and future execution times',
      'Note: Both 0 and 7 represent Sunday in the weekday field',
      'Use crontab -e on Linux servers to edit scheduled tasks'
    ]
  },
  qrGenerator: {
    title: 'QR Code Generator',
    subtitle: 'Generate QR codes online with customizable size, colors, and error correction',
    contentLabel: 'QR Content',
    contentPlaceholder: 'Enter text, URL, phone number, etc...',
    sizeLabel: 'Image Size',
    fgColor: 'Foreground',
    bgColor: 'Background',
    ecLevel: 'Error Correction',
    ecUpgradeHint: 'Error correction is auto-upgraded to H when a logo is added to ensure scannability',
    moduleStyle: 'Module Style',
    styles: {
      square: 'Square',
      rounded: 'Rounded',
      dots: 'Dots',
      diamond: 'Diamond'
    },
    logoLabel: 'Logo Image',
    logoUploadHint: 'Click or drag & drop to upload a logo',
    logoRemove: 'Remove Logo',
    logoSize: 'Logo Size',
    emptyHint: 'Enter content to generate QR code',
    downloadPNG: 'Download PNG',
    downloadSVG: 'Download SVG',
    copy: 'Copy Image',
    copySuccess: 'Copied to clipboard!',
    copyFail: 'Copy failed, please save manually',
    whatIs: 'What is a QR Code?',
    whatIsDesc: 'A QR Code (Quick Response Code) is a type of 2D barcode that can store text, URLs, contact information, and more. QR codes are widely used in payments, business card sharing, product tracing, and website linking. This tool generates customizable QR codes entirely in your browser.',
    useCasesTitle: 'Common Use Cases',
    useCases: [
      'URL sharing: Convert web addresses to QR codes for easy mobile access',
      'WiFi connection: Generate WiFi credentials as QR codes for quick connect',
      'Business cards: Encode contact info as QR codes for quick adding',
      'Payments: Generate payment QR codes for transfers',
      'Product tracing: Encode product info as QR codes for packaging'
    ],
    tipsTitle: 'Tips',
    tips: [
      'Higher error correction (H > Q > M > L) makes more complex but more resilient QR codes',
      'Use dark foreground with light background for optimal scanning contrast',
      'When a logo is added, error correction is auto-upgraded to H. Keep logo under 25% for reliable scanning',
      'Always test scan the generated QR code before using it',
      'SVG format is ideal for print, allowing lossless scaling to any size'
    ]
  },
  qrScanner: {
    title: 'QR Code Scanner',
    subtitle: 'Upload images to decode QR codes, supports drag & drop and paste',
    uploadHint: 'Click to upload or drag image here',
    uploadSub: 'Supports JPG, PNG, GIF, WebP formats',
    resultLabel: 'Scan Result',
    copy: 'Copy',
    copySuccess: 'Copied!',
    copyFail: 'Copy failed',
    openLink: 'Open link in new tab',
    scanning: 'Scanning...',
    emptyResult: 'Upload an image with a QR code to scan',
    noQrFound: 'No QR code detected in the image. Please check if the image contains a clear QR code.',
    libLoading: 'Scanner library is loading, please retry',
    reset: 'Reset',
    pasteHint: 'You can also press Ctrl+V (Cmd+V) to paste a screenshot for scanning',
    whatIs: 'What is QR Code Scanning?',
    whatIsDesc: 'QR code scanning extracts encoded information from QR code images. By uploading an image containing a QR code, this tool automatically identifies and decodes the text, URL, or other content within. All image processing is done locally in your browser, protecting your privacy.',
    tipsTitle: 'Tips',
    tips: [
      'Ensure the QR code image is clear and not obstructed',
      'Supports pasting screenshots directly (Ctrl+V / Cmd+V)',
      'Supports dragging image files to the upload area',
      'If scanning fails, try cropping the image so the QR code takes up more area',
      'All processing is local - no images are uploaded to any server'
    ]
  },
  taxRate: {
    title: 'Tax Rate Calculator',
    subtitle: 'Calculate tax amounts with common VAT rates, supports tax-inclusive/exclusive conversion',
    modeIncluded: 'Tax-Inclusive Amount',
    modeExcluded: 'Tax-Exclusive Amount',
    amountIncluded: 'Tax-Inclusive Amount',
    amountExcluded: 'Tax-Exclusive Amount',
    inputPlaceholder: 'Enter amount',
    yuan: 'CNY',
    rateLabel: 'Tax Rate',
    taxAmount: 'Tax Amount',
    excludedAmount: 'Excluding Tax',
    includedAmount: 'Including Tax',
    rateUsed: 'Tax Rate',
    commonRatesTitle: 'Common VAT Rates',
    tableRate: 'Rate',
    tableScope: 'Applicable Scope',
    rateItems: [
      { rate: '13%', scope: 'Sale of goods, processing/repair services, tangible property leasing, etc.' },
      { rate: '9%', scope: 'Transportation, postal, telecom, construction, real estate leasing, agriculture, etc.' },
      { rate: '6%', scope: 'Financial services, modern services, lifestyle services, intangible assets, etc.' },
      { rate: '5%', scope: 'Real estate leasing (simplified), property sales (simplified), etc.' },
      { rate: '3%', scope: 'Small-scale taxpayer standard rate' },
      { rate: '1%', scope: 'Small-scale taxpayer reduced rate (preferential policy)' }
    ],
    whatIs: 'What is Tax Rate Calculation?',
    whatIsDesc: 'Tax rate calculation determines the tax amount based on a price and applicable tax rate. In business, you often need to convert between tax-inclusive and tax-exclusive prices. This tool supports bidirectional calculation to help you quickly complete tax-related computations. All calculations are done locally in your browser.',
    formulaTitle: 'Formulas',
    formulas: [
      'Tax-inclusive = Tax-exclusive × (1 + Tax rate)',
      'Tax-exclusive = Tax-inclusive ÷ (1 + Tax rate)',
      'Tax amount = Tax-inclusive - Tax-exclusive',
      'Tax amount = Tax-exclusive × Tax rate'
    ]
  },
  listSum: {
    title: 'List Sum Calculator',
    subtitle: 'Quickly sum a list of numbers with average, max, min, and median statistics',
    inputLabel: 'Enter Number List',
    inputPlaceholder: 'Enter one number per line, e.g.:\n100\n200.5\n300\n-50\n150.75',
    separatorLabel: 'Separator:',
    sepLine: 'Line',
    sepComma: 'Comma',
    sepSpace: 'Space',
    sepTab: 'Tab',
    ignoreNonNumber: 'Ignore non-numbers',
    decimalMode: 'High precision',
    sum: 'Sum',
    average: 'Average',
    count: 'Count',
    max: 'Maximum',
    min: 'Minimum',
    median: 'Median',
    parsedList: 'Parsed Numbers',
    skippedInfo: 'Skipped {n} non-numeric items',
    whatIs: 'What is List Sum?',
    whatIsDesc: 'The List Sum calculator quickly sums a group of numbers while automatically calculating average, maximum, minimum, and median statistics. It supports multiple separators (line break, comma, space, tab) and can handle data pasted from spreadsheets or documents. All calculations are done locally in your browser.',
    tipsTitle: 'Tips',
    tips: [
      'You can paste a column of data directly from Excel or other spreadsheet software',
      'Supports negative numbers and decimals, e.g. -100 and 3.14',
      'Supports thousand-separated numbers like 1,234,567.89',
      'Enable "Ignore non-numbers" to automatically filter text mixed with numbers',
      'Switch separator modes to handle different data formats'
    ]
  },
  loanCalc: {
    title: 'Loan Calculator',
    subtitle: 'Universal loan repayment calculator with equal installment and equal principal methods',
    amountLabel: 'Loan Amount',
    yuan: 'CNY',
    rateLabel: 'Annual Rate',
    termLabel: 'Loan Term',
    months: 'months',
    quickTerm: 'Quick select:',
    methodEqual: 'Equal Installment',
    methodPrincipal: 'Equal Principal',
    monthlyPayment: 'Monthly Payment',
    firstMonthPayment: 'First Month',
    lastMonthPayment: 'Last Month',
    totalInterest: 'Total Interest',
    totalPayment: 'Total Payment',
    scheduleTitle: 'Repayment Schedule',
    show: 'Show',
    hide: 'Hide',
    period: 'Period',
    monthlyPaymentCol: 'Payment',
    principalCol: 'Principal',
    interestCol: 'Interest',
    remainingCol: 'Remaining',
    whatIs: 'What is a Loan Calculator?',
    whatIsDesc: 'A loan calculator helps you compute monthly payments, total interest, and total repayment based on loan amount, annual interest rate, and repayment term. It supports both equal installment and equal principal repayment methods. Useful for consumer loans, auto loans, business loans, and more. All calculations are done locally in your browser.',
    methodsTitle: 'Repayment Methods',
    methods: [
      'Equal Installment: Fixed monthly payment throughout the term. Interest portion is higher initially and decreases over time. Suitable for borrowers with stable income.',
      'Equal Principal: Fixed principal portion each month with decreasing interest. Monthly payment decreases over time. Higher initial payments but lower total interest.'
    ],
    tipsTitle: 'Suggestions',
    tipsList: [
      'Compare total interest between the two methods to choose the best plan',
      'Equal installment has higher total interest but fixed payments for easier budgeting',
      'Equal principal has lower total interest but higher initial monthly payments',
      'Check current bank interest rate policies for the best rates before borrowing'
    ]
  },
  blog: {
    title: 'Tech Blog',
    subtitle: 'Practical tutorials and in-depth knowledge sharing',
    readMore: 'Read More',
    backToList: 'Back to Articles',
    notFound: 'Article Not Found',
    authorName: 'MoonRise Tools Editorial Team',
    authorBio: 'Dedicated to sharing practical technical knowledge and tool usage tips to help users improve their work efficiency.',
    articles: {
      'json-guide': {
        title: 'The Complete JSON Guide: A Practical Tutorial from Beginner to Expert',
        category: 'Development',
        date: '2026-02-10',
        readTime: 'About 8 min',
        summary: 'JSON is one of the most important data formats in modern web development. This article covers JSON basics, syntax rules, common errors, best practices, and practical application tips.',
        sections: [
          {
            heading: 'The Birth of JSON',
            text: 'In 2001, Douglas Crockford proposed the JSON (JavaScript Object Notation) format. Before that, web applications mainly used XML for data transmission, but XML syntax was verbose and parsing was complex. JSON quickly became the preferred format for Web APIs and configuration files due to its concise syntax and natural affinity with JavaScript. Today, virtually all major programming languages have built-in JSON parsing libraries.'
          },
          {
            heading: 'The Six JSON Data Types',
            text: 'JSON supports six data types. Understanding them is the foundation for using JSON correctly:',
            list: [
              'String: Must be wrapped in double quotes, e.g., "hello". Single quotes are not allowed — this is the most common beginner mistake',
              'Number: Supports integers and floating-point numbers, e.g., 42, 3.14, -10. NaN and Infinity are not supported',
              'Boolean: Only true and false (must be lowercase)',
              'Null: Represents null value, must be written as null (lowercase)',
              'Object: A collection of key-value pairs wrapped in curly braces {}. Keys must be strings',
              'Array: An ordered list of values wrapped in square brackets []. Elements can be any type'
            ]
          },
          {
            heading: 'Why JSON Formatting Matters',
            text: 'In real development, JSON data is usually minified (no spaces or line breaks), which reduces transmission size but makes it extremely hard to read and debug. Formatting (Pretty Print) converts compressed JSON into a readable format with indentation and line breaks. Good formatting tools also validate JSON syntax, helping developers quickly locate errors. Our JSON formatting tool provides one-click formatting and syntax validation.'
          },
          {
            heading: 'Common JSON Syntax Errors and Solutions',
            list: [
              'Trailing commas: No comma after the last element. {"a":1, "b":2,} is invalid',
              'Single quotes: JSON standard only allows double quotes. {\'name\': \'test\'} is invalid',
              'Comments: JSON standard does not support comments. Consider JSON5 or JSONC if you need comments',
              'Unescaped special characters: Double quotes, backslashes, newlines in strings need escaping',
              'Unquoted keys: All key names must be wrapped in double quotes. {name: "test"} is invalid'
            ]
          },
          {
            heading: 'JSON Best Practices in Frontend-Backend Communication',
            text: 'In modern frontend-backend separated architectures, JSON is the most commonly used data exchange format. Here are some practical recommendations:',
            list: [
              'Unified response format: Use {code, message, data} three-layer structure for consistent frontend handling',
              'Standardized date format: Use ISO 8601 (e.g., 2026-02-10T08:30:00Z) to avoid timezone confusion',
              'Field naming conventions: Use camelCase (e.g., userName) or snake_case (e.g., user_name) consistently',
              'Pagination structure: List APIs should return {list, total, page, pageSize} for easy frontend pagination',
              'Sensitive data filtering: Backend should filter passwords, keys, and other sensitive fields from JSON responses'
            ]
          },
          {
            heading: 'From JSON to JSON Schema',
            text: 'JSON Schema is a specification for describing JSON data structures. It can define the type, required status, value range, and other constraints for each field in a JSON object. In large projects, JSON Schema enables automatic API documentation generation, request parameter validation, and IDE intelligent hints, significantly improving development efficiency and code quality.'
          }
        ]
      },
      'timestamp-explained': {
        title: 'Timestamps Explained: Essential Time Handling Knowledge for Developers',
        category: 'Development',
        date: '2026-02-08',
        readTime: 'About 7 min',
        summary: 'Time handling is a common but error-prone area in software development. This article explains how Unix timestamps work, proper timezone handling, and time operations in different programming languages.',
        sections: [
          {
            heading: 'Why Is Time Handling So Complex?',
            text: 'The seemingly simple concept of time is full of complexity in computers. People in different time zones see different times, daylight saving time makes clocks jump forward and back, leap seconds occasionally make a minute 61 seconds long, and date formats vary by country. These factors combined make time handling one of the most bug-prone areas in programming. Unix timestamps were created to simplify these complexities.'
          },
          {
            heading: 'How Unix Timestamps Work',
            text: 'A Unix timestamp is a simple integer representing the number of seconds elapsed since January 1, 1970 00:00:00 UTC. Its core advantage is timezone independence. Whether you are in London (UTC+0) or Tokyo (UTC+9), the timestamp for the same moment is the same number. This makes it ideal for storing and transmitting time information in cross-timezone systems.'
          },
          {
            heading: 'Choosing Between Seconds and Milliseconds',
            text: 'Second-precision timestamps (10 digits) are sufficient for most scenarios like logging and user registration times. Millisecond-precision timestamps (13 digits) are suitable for higher precision needs like financial transactions, performance monitoring, and animation frame timing. JavaScript\'s Date.now() returns millisecond timestamps by default, while Python\'s time.time() returns second timestamps with decimals. Always unify timestamp precision in frontend-backend communication.'
          },
          {
            heading: 'Proper Timezone Handling',
            list: [
              'Storage: Always store UTC time or timestamps in databases, never local time',
              'Display: Convert to local time based on the user\'s timezone on the frontend',
              'Transmission: Use UTC time or timestamps in API interfaces, avoid transmitting local time',
              'Logging: Use UTC time in server logs for easier cross-timezone debugging',
              'User preference: Allow users to set their timezone preference in the system'
            ]
          },
          {
            heading: 'The Year 2038 Problem',
            text: '32-bit systems store timestamps as signed 32-bit integers with a maximum value of 2147483647, corresponding to January 19, 2038 03:14:07 UTC. After this point, 32-bit timestamps will overflow to negative numbers. While most modern systems have migrated to 64-bit, some embedded systems and legacy software remain at risk. If you are developing long-running systems, ensure you use 64-bit timestamps.'
          },
          {
            heading: 'Recommended Time Handling Libraries',
            text: 'In real development, directly manipulating timestamps is simple but error-prone. Use mature time handling libraries: JavaScript recommends day.js (lightweight) or date-fns (functional); Python recommends the built-in datetime module with pytz for timezone support; Java recommends the java.time package (Java 8+). These libraries encapsulate common operations like timezone conversion, formatting, and date calculations, significantly reducing time-related bugs.'
          }
        ]
      },
      'mortgage-tips': {
        title: 'Complete Mortgage Guide: How to Choose the Best Repayment Plan',
        category: 'Finance',
        date: '2026-02-05',
        readTime: 'About 10 min',
        summary: 'Buying a home is one of the most important financial decisions for most families. This article analyzes different repayment methods and provides mortgage planning advice for various income levels.',
        sections: [
          {
            heading: 'Mortgage Basics',
            text: 'A mortgage is a loan from a bank where the purchased property serves as collateral. Understanding key terms is essential: principal (the amount borrowed), interest rate (annual cost of borrowing), term (repayment period, typically 15-30 years), and down payment (initial payment, usually 20-30% of the property price). Choosing the right mortgage type is the first step in controlling costs.'
          },
          {
            heading: 'Fixed vs Variable Rate: A Deep Comparison',
            text: 'Fixed-rate mortgages keep the same interest rate throughout the loan term, providing predictable monthly payments. Variable-rate mortgages (ARMs) start with a lower rate that adjusts periodically based on market conditions. For example, on a $300,000 loan at 6.5% fixed for 30 years, the monthly payment is about $1,896 with total interest of $382,500. With a 5/1 ARM starting at 5.5%, the initial payment is $1,703, but could increase significantly after the fixed period ends.'
          },
          {
            heading: 'Income-Based Mortgage Guidelines',
            list: [
              'Income under $60K/year: Keep monthly payment under 28% of gross income, consider smaller properties or longer terms',
              'Income $60K-$100K/year: Both fixed and variable rates work, build a 6-month emergency fund first',
              'Income over $100K/year: Consider 15-year terms to save on total interest, can handle higher monthly payments',
              'Dual-income households: Base affordability on one income so you can survive if one person loses their job',
              'Self-employed: Lenders may require 2 years of tax returns, consider saving for a larger down payment'
            ]
          },
          {
            heading: 'Prepayment Strategies',
            text: 'Paying extra on your mortgage can save thousands in interest. Even an additional $100/month on a $300,000 loan can save over $30,000 in interest and shorten the term by 5 years. However, consider your overall financial picture first: pay off high-interest debt, build an emergency fund, and maximize retirement contributions before accelerating mortgage payments. Some loans have prepayment penalties, so check your terms carefully.'
          },
          {
            heading: 'Understanding Interest Rate Trends',
            text: 'Interest rates are influenced by central bank policies, inflation, economic growth, and global market conditions. When rates are rising, locking in a fixed rate provides stability. When rates are falling, variable rates or refinancing can save money. Monitor economic indicators and rate forecasts to make informed decisions about when to apply for a mortgage or refinance an existing one.'
          },
          {
            heading: 'Key Tips Before Applying',
            list: [
              'Check and improve your credit score at least 6 months before applying',
              'Save for a 20% down payment to avoid private mortgage insurance (PMI)',
              'Get pre-approved by multiple lenders to compare rates and terms',
              'Factor in additional costs: property taxes, insurance, HOA fees, maintenance',
              'Keep a reserve fund of 3-6 months of payments for emergencies'
            ]
          }
        ]
      },
      'markdown-writing': {
        title: 'Markdown Writing Guide: Make Your Text Formatting More Elegant',
        category: 'Productivity',
        date: '2026-02-01',
        readTime: 'About 6 min',
        summary: 'Markdown is an essential writing tool for tech professionals and content creators. This article teaches you how to write efficiently with Markdown, including common syntax, advanced techniques, and best practices.',
        sections: [
          {
            heading: 'Why You Should Learn Markdown',
            text: 'In the digital writing era, we frequently switch between platforms: writing blogs, taking notes, creating documentation, posting on forums. Each platform has different formatting methods. Markdown provides a unified solution: using simple plain text markers to represent formatting. Learn Markdown, and you can write efficiently on GitHub, Notion, Obsidian, and virtually all major platforms.'
          },
          {
            heading: 'Master Core Markdown Syntax in 5 Minutes',
            text: 'Markdown\'s core syntax is extremely concise. Just remember these key markers:',
            list: [
              'Headings: Use # signs. One # for H1, two ## for H2, up to six levels',
              'Bold: Wrap with **bold text**',
              'Italic: Wrap with *italic text*',
              'Links: [link text](URL) format',
              'Images: ![alt text](image URL) format',
              'Lists: Unordered with - prefix, ordered with 1. 2. 3. prefix',
              'Blockquotes: Start with >',
              'Code: Inline with backticks, code blocks with triple backticks'
            ]
          },
          {
            heading: 'Advanced Markdown Techniques',
            list: [
              'Tables: Use | to separate columns, --- to separate header from content. Use :--- for left align, :---: for center, ---: for right align',
              'Task lists: Use - [ ] for unchecked tasks, - [x] for checked tasks',
              'Footnotes: Use [^1] for reference, define with [^1]: content at the bottom',
              'Strikethrough: Wrap with ~~deleted text~~',
              'Horizontal rules: Use three or more --- or ***',
              'Nested lists: Indent by two spaces under a list item to create sub-lists'
            ]
          },
          {
            heading: 'Markdown Writing Best Practices',
            text: 'Good Markdown documents go beyond correct syntax — they focus on structure and readability:',
            list: [
              'Use logical heading hierarchy, don\'t skip levels (H1 directly to H3)',
              'Leave blank lines between paragraphs for clear visual separation',
              'Always specify language type in code blocks for syntax highlighting',
              'Add descriptive alt text to images for accessibility',
              'Use table of contents (TOC) for long documents to help readers navigate',
              'Keep filenames concise and meaningful, using hyphens to separate words'
            ]
          },
          {
            heading: 'Recommended Markdown Editors',
            text: 'Different scenarios call for different editors. For lightweight note-taking, try Obsidian with its bidirectional linking and knowledge graph. For technical documentation, VS Code with Markdown extensions works great alongside code. For online collaboration, consider HackMD or Notion. If you need a quick, no-install editor, our online Markdown editor is a great choice with real-time preview and one-click HTML copy.'
          }
        ]
      },
      'image-optimization': {
        title: 'Complete Web Image Optimization Handbook: Key Techniques for Better Performance',
        category: 'Frontend',
        date: '2026-01-28',
        readTime: 'About 9 min',
        summary: 'Images typically account for over 60% of total webpage size. This article covers image format characteristics, compression strategies, lazy loading techniques, and modern web image best practices.',
        sections: [
          {
            heading: 'Why Images Are the Biggest Performance Bottleneck',
            text: 'According to HTTP Archive data, images account for 50%-65% of total webpage size on average. An unoptimized webpage might contain dozens of high-resolution images totaling several megabytes. For mobile users and those with poor network conditions, this means extremely long load times and high data usage. Google\'s research shows that each additional second of page load time increases bounce rate by 32%. Therefore, image optimization is one of the most effective ways to improve website performance.'
          },
          {
            heading: 'Five Image Formats Explained',
            list: [
              'JPEG/JPG: Lossy compression format ideal for photos and color-rich images. Quality settings of 75-85% offer the best balance between file size and visual quality. Does not support transparency',
              'PNG: Lossless compression format supporting transparency. PNG-8 supports up to 256 colors for simple graphics; PNG-24 supports 16 million colors for complex transparent images. Files are typically larger than JPEG',
              'WebP: Modern format by Google, 25-34% smaller than JPEG and 26% smaller than PNG at equivalent quality. Supports both lossy and lossless compression, transparency, and animation. Now supported by all major browsers',
              'GIF: Supports animation and transparency but limited to 256 colors. Good for simple animated icons, not suitable for photos. For complex animations, use WebP or video formats instead',
              'SVG: Vector graphics format using XML, scales infinitely without quality loss. Perfect for icons, logos, and simple illustrations. Extremely small files that can be styled with CSS'
            ]
          },
          {
            heading: 'The Science of Image Compression',
            text: 'Image compression is divided into lossy and lossless types. Lossy compression discards details imperceptible to the human eye to reduce file size — JPEG is a classic lossy format. Lossless compression reduces file size through more efficient encoding without losing any information — PNG uses lossless compression. In practice, use lossy compression for photos (quality 75-85%) and lossless compression for graphics requiring exact reproduction.'
          },
          {
            heading: 'Responsive Images and Lazy Loading',
            text: 'In modern web development, responsive images and lazy loading are two key optimization techniques. Responsive images use HTML\'s srcset attribute to let browsers automatically select the appropriate resolution based on device screen size and pixel density, avoiding loading desktop-sized images on mobile. Lazy loading only loads images when they enter the visible viewport, dramatically reducing initial page load time. Modern browsers natively support the loading="lazy" attribute.'
          },
          {
            heading: 'Benefits of Image CDNs',
            list: [
              'Automatic format conversion: Serve WebP or AVIF based on browser support',
              'Dynamic resizing: Specify image dimensions via URL parameters, no manual cropping needed',
              'Global acceleration: Cache images at edge nodes worldwide, serving from the nearest location',
              'Smart compression: Automatically adjust compression levels based on network quality',
              'Bandwidth savings: Typically reduces image traffic by 40%-70%'
            ]
          },
          {
            heading: 'Practical Recommendations',
            text: 'Our image processing tool provides local image compression — all processing happens in your browser without uploading any data to servers. You can adjust quality, scale, apply filters, and preview compression results in real-time. Before uploading images to your website, use the tool to compress them to a reasonable size. For web display, keeping images under 100-200KB is a good target.'
          }
        ]
      }
    }
  },
  base64: {
    title: 'Base64 Encoder/Decoder',
    subtitle: 'Online Base64 encoding and decoding tool with support for Unicode and special characters',
    inputLabel: 'Input Text',
    outputLabel: 'Output Result',
    inputPlaceholder: 'Enter text to encode or decode...',
    outputPlaceholder: 'Result will be displayed here...',
    encode: 'Encode',
    decode: 'Decode',
    swap: 'Swap Input/Output',
    encodeSuccess: 'Base64 encoding successful!',
    encodeError: 'Encoding failed, please check the input',
    decodeSuccess: 'Base64 decoding successful!',
    decodeError: 'Decoding failed, please ensure the input is a valid Base64 string',
    whatIs: 'What is Base64?',
    whatIsDesc: 'Base64 is an encoding method that represents binary data using 64 printable characters. It is commonly used in scenarios where binary data needs to be processed as text, such as email attachment encoding, embedding data in URLs, and inline images in CSS (Data URI). Base64-encoded data is approximately 33% larger than the original, but ensures safe transmission. All encoding/decoding operations are performed locally in your browser.',
    useCasesTitle: 'Common Use Cases',
    useCases: [
      'Email attachments: MIME protocol uses Base64 to encode email attachments',
      'Data URI: Embed small images or fonts directly in HTML/CSS',
      'API transmission: Safely pass binary data in JSON or URLs',
      'JWT Token: JSON Web Tokens use Base64 to encode header and payload sections',
      'Data storage: Store binary data as text in databases or configuration files'
    ],
    tipsTitle: 'Tips',
    tips: [
      'This tool supports Unicode characters using UTF-8 encoding internally',
      'Base64 is NOT encryption — do not use it to protect sensitive data',
      'Encoded strings contain only A-Z, a-z, 0-9, +, / and = characters',
      'If decoding fails, verify that the input is a valid Base64 string'
    ]
  },
  urlCodec: {
    title: 'URL Encoder/Decoder',
    subtitle: 'Online URL encoding and decoding tool supporting encodeURI and encodeURIComponent',
    inputLabel: 'Input Text',
    outputLabel: 'Output Result',
    inputPlaceholder: 'Enter URL or text to encode/decode...',
    outputPlaceholder: 'Result will be displayed here...',
    encode: 'encodeURI',
    decode: 'Decode',
    encodeComponent: 'encodeURIComponent',
    swap: 'Swap Input/Output',
    encodeSuccess: 'Encoding successful!',
    decodeSuccess: 'Decoding successful!',
    decodeError: 'Decoding failed, please check the input',
    error: 'Encoding error',
    whatIs: 'What is URL Encoding?',
    whatIsDesc: 'URL encoding (also called percent-encoding) converts special characters into %XX format for safe URL transmission. URL specifications only allow a subset of ASCII characters; Chinese characters, spaces, and special symbols must be encoded. JavaScript provides encodeURI and encodeURIComponent with different encoding scopes. All operations are performed locally in your browser.',
    useCasesTitle: 'Common Use Cases',
    useCases: [
      'URL parameters: Encode special characters in URL query parameters',
      'API requests: Ensure query parameters are correctly formatted in GET requests',
      'Form submission: Browsers automatically URL-encode form data',
      'Frontend routing: Encode special characters in dynamic route parameters',
      'Link sharing: Ensure links with special characters open correctly'
    ],
    tipsTitle: 'Tips',
    tips: [
      'encodeURI: Encodes the full URL, preserving :/?#[]@!$&\'()*+,;= URL functional characters',
      'encodeURIComponent: Encodes URL parameter values, encoding all special characters except -_.!~*\'()',
      'Use encodeURIComponent for parameter values, encodeURI for complete URLs',
      'If decoding produces garbled text, the encoding format may not match — check the original encoding'
    ]
  },
  hashTool: {
    title: 'MD5/SHA Hash Calculator',
    subtitle: 'Calculate MD5, SHA-1, SHA-256, SHA-384, and SHA-512 hash values online',
    inputLabel: 'Input Text',
    inputPlaceholder: 'Enter text to calculate hash values...',
    outputCase: 'Output Format',
    upper: 'Uppercase',
    lower: 'Lowercase',
    whatIs: 'What is a Hash Algorithm?',
    whatIsDesc: 'A hash algorithm converts input data of any length into a fixed-length output (hash value/digest). Good hash algorithms are one-way (cannot reverse the original data from the hash), deterministic (same input always produces same output), and collision-resistant (hard to find two different inputs producing the same output). Hash algorithms are widely used for data integrity verification, password storage, and digital signatures. All calculations are performed locally in your browser.',
    algosTitle: 'Supported Hash Algorithms',
    algos: [
      'MD5: 128-bit output, fast computation, no longer recommended for security — suitable for file verification',
      'SHA-1: 160-bit output, proven insecure and being phased out',
      'SHA-256: 256-bit output, the most commonly used secure hash algorithm, core algorithm of Bitcoin',
      'SHA-384: 384-bit output, extended version of SHA-256 with higher security',
      'SHA-512: 512-bit output, highest security level for the most demanding scenarios'
    ],
    useCasesTitle: 'Common Use Cases',
    useCases: [
      'File integrity: Compare hash values of downloaded files with official values to verify no tampering',
      'Password storage: Store hash values of user passwords in databases instead of plaintext',
      'Data deduplication: Quickly determine if two pieces of data are identical by comparing hashes',
      'Digital signatures: Generate hash values for documents or code combined with asymmetric encryption',
      'Blockchain: One of the core foundations of Bitcoin and other blockchain technologies'
    ]
  },
  unicodeConverter: {
    title: 'Unicode Converter',
    subtitle: 'Convert between Unicode, Hex, HTML entities, and UTF-8 encodings',
    inputLabel: 'Input Text',
    outputLabel: 'Conversion Result',
    inputPlaceholder: 'Enter text or encoded string to convert...',
    outputPlaceholder: 'Conversion result will be displayed here...',
    convert: 'Convert →',
    reverse: 'Reverse Convert',
    mode: {
      unicode: 'Unicode (\\uXXXX)',
      hex: 'Hexadecimal (\\xXX)',
      htmlEntity: 'HTML Entity (&#NNN;)',
      utf8Hex: 'UTF-8 Hex'
    },
    whatIs: 'What is Unicode?',
    whatIsDesc: 'Unicode is an international character encoding standard that provides a unified encoding for all written languages and symbols worldwide. Unicode defines over 140,000 characters covering virtually all human languages. In programming, conversions between different encoding formats are often needed — for example, converting Chinese characters to \\uXXXX format for JSON transmission, or converting special characters to HTML entities for web display. This tool supports conversions between multiple encoding formats, all performed locally in your browser.',
    useCasesTitle: 'Common Use Cases',
    useCases: [
      'JSON encoding: Use \\uXXXX format to represent non-ASCII characters in JSON',
      'HTML encoding: Use HTML entities to prevent special characters from being misinterpreted',
      'JavaScript escaping: Use \\xXX or \\uXXXX to represent special characters in JS code',
      'Internationalization: Handle encoding conversions for multilingual text',
      'Data debugging: View underlying encoding representation to troubleshoot encoding issues'
    ]
  },
  morseCode: {
    title: 'Morse Code Converter',
    subtitle: 'Convert between text and Morse code with Chinese support and audio playback',
    modeLabel: 'Encoding Mode',
    modeBasic: 'Basic',
    modeChinese: 'Chinese',
    modeBasicHint: 'Supports English letters, numbers, and common punctuation',
    modeChineseHint: 'Supports mixed Chinese/English via Unicode encoding',
    textLabel: 'Plain Text',
    morseLabel: 'Morse Code',
    textPlaceholder: 'Enter text here...',
    textPlaceholderCn: 'Enter Chinese/English text (e.g., 你好 World)...',
    morsePlaceholder: 'Morse code will appear here (spaces separate letters, / separates words)...',
    encode: 'To Morse Code',
    decode: 'To Text',
    play: 'Play',
    chartTitle: 'Morse Code Reference Chart',
    encodeSuccess: 'Converted to Morse code!',
    decodeSuccess: 'Converted to text!',
    cnEncodingTitle: 'Chinese Encoding Principle',
    cnEncodingDesc: 'In Chinese mode, each Chinese character is first converted to its Unicode code point in hexadecimal (4 digits), then each hex digit is encoded as standard Morse code, wrapped in square brackets [ ] to represent a complete Chinese character. English letters and numbers are encoded using standard Morse code. Examples:',
    whatIs: 'What is Morse Code?',
    whatIsDesc: 'Morse Code was invented by American inventor Samuel Morse in 1836 as a method of encoding characters using sequences of dots (·) and dashes (-). It was one of the earliest forms of digital communication and was widely used in telegraph communications. Although modern communication technology has replaced the telegraph, Morse code is still used in aviation, maritime, amateur radio, and emergency signaling (SOS: ··· --- ···).',
    useCasesTitle: 'Common Use Cases',
    useCases: [
      'Education: Learn about early communication technology and encoding principles',
      'Emergency signaling: SOS (··· --- ···) is a universally recognized distress signal',
      'Amateur radio: Radio enthusiasts worldwide still communicate using Morse code',
      'Cryptography introduction: Morse code is a great starting point for understanding encoding',
      'Cultural fun: Create unique messages, jewelry, or designs using Morse code patterns'
    ]
  },
  passwordGen: {
    title: 'Random Password Generator',
    subtitle: 'Generate secure random passwords with customizable rules and batch generation',
    placeholder: 'Click generate to create a password',
    length: 'Password Length',
    uppercase: 'Uppercase Letters',
    lowercase: 'Lowercase Letters',
    numbers: 'Numbers',
    symbols: 'Special Symbols',
    excludeAmbiguous: 'Exclude Ambiguous Characters (0Oo1Il)',
    generate: 'Generate',
    strengthLabel: 'Password Strength',
    weak: 'Weak',
    medium: 'Medium',
    strong: 'Strong',
    batchTitle: 'Batch Generation',
    batchCount: 'Count',
    batchGenerate: 'Generate Batch',
    whatIs: 'Why Do You Need Strong Passwords?',
    whatIsDesc: 'Weak passwords are one of the biggest cybersecurity threats. Common passwords like "123456" and "password" can be cracked in seconds. Secure passwords should be long enough (at least 12 characters), include multiple character types (uppercase, lowercase, numbers, symbols), and be unique for each account. This tool uses the browser\'s native cryptographic random number generator (crypto.getRandomValues) to ensure true randomness. All passwords are generated locally in your browser and never sent to any server.',
    tipsTitle: 'Password Security Tips',
    tips: [
      'Use at least 12 characters, 16+ is more secure',
      'Mix uppercase letters, lowercase letters, numbers, and special symbols',
      'Use a different password for each important account',
      'Use a password manager (like 1Password, Bitwarden) to store passwords',
      'Enable two-factor authentication (2FA) for additional account protection',
      'Avoid using personal information (birthday, name) as part of your password'
    ]
  },
  uuidGen: {
    title: 'UUID/GUID Generator',
    subtitle: 'Generate Universally Unique Identifiers online with v1 and v4 versions',
    version: 'UUID Version',
    caseLabel: 'Case',
    lower: 'Lowercase',
    upper: 'Uppercase',
    withBraces: 'Add Braces {}',
    noDashes: 'Remove Dashes -',
    batchTitle: 'Batch Generation',
    batchCount: 'Count',
    batchGenerate: 'Generate Batch',
    whatIs: 'What is UUID?',
    whatIsDesc: 'UUID (Universally Unique Identifier) is a 128-bit identifier designed to be globally unique. The format is xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx (32 hex characters separated by 4 hyphens). The most common versions are v4 (random-based) and v1 (time-based with MAC address). UUIDs are widely used for database primary keys, distributed systems, session identifiers, and other scenarios requiring unique identifiers.',
    useCasesTitle: 'Common Use Cases',
    useCases: [
      'Database primary keys: Avoid security risks and distributed conflicts from auto-increment IDs',
      'Distributed systems: Generate non-conflicting unique identifiers across service nodes',
      'Session management: Generate unique session IDs for user sessions',
      'API request tracking: Assign unique IDs to requests for log tracing',
      'File naming: Use UUIDs for uploaded file names to avoid conflicts'
    ],
    tipsTitle: 'Tips',
    tips: [
      'UUID v4 is random-based, suitable for most scenarios, simple and secure',
      'UUID v1 contains time information, sortable by time, suitable for sequential needs',
      'Store UUIDs without dashes in databases to save space',
      'UUID collision probability is extremely low (~1 in 2^122), practically unique',
      'For shorter unique IDs, consider alternatives like NanoID'
    ]
  }
}